import React, { useState, useEffect } from "react";
import { supabase, signUp, signIn, signOut, getCurrentUser, onAuthChange, resetPasswordForEmail, updatePassword, saveResponse, getSavedResponses, deleteSavedResponse } from "./supabaseClient";
import { trackPageView, trackLead, trackInitiateCheckout, trackPurchase } from "./lib/pixel";
import { PrivacyPolicy, TermsOfService, Disclaimer } from "./Legal";
import { HowToUse, FAQ } from "./Help";
import ConceptMapView from "./ConceptMapView";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { marked } from "marked";

const B = { navy:"#12172B", navy2:"#1A2040", pink:"#FF2D78", yellow:"#F5E200", cyan:"#00C8E8", green:"#00D084", red:"#CC0000", purple:"#7B2FBE" };

const PLANS = [
  { id:"free",   name:"Free",              price:"$0",  period:"",          color:B.cyan,   features:["5 free AI sessions (one-time trial)","23 of 24 tools (EKG is Pro)","Save responses"], cta:"Go Free", current:"Current plan" },
  { id:"pro",    name:"Pro",               price:"$12", period:"/month",    color:B.pink,   stripeLink:"https://buy.stripe.com/dRmfZi2NB75Q4NO7wXc3m00",    features:["Unlimited AI sessions","All 24 tools — including EKG photo reads","Saved responses library","Cancel anytime"], cta:"Go Pro — $12/mo", current:"✓ You're Pro", badge:"Most Popular" },
  { id:"bundle", name:"Founding Member", price:"$97", period:" lifetime", color:B.yellow, stripeLink:"https://buy.stripe.com/3cI14o3RF3TEbccaJ9c3m01", description:"Lifetime unlimited access — 24 tools — every future tool we build — forever. Never pay again.", urgencyText:"When founding member pricing ends, price goes to $12/month — forever", features:["Lifetime unlimited access","RN101: bestseller how to survive your first year of nursing","Rapid Response Mode guide","Your Patient's Labs Are Talking to You — ebook + workbook","100 SBAR Scenarios — Stop Freezing. Start SBARing.","Critical Thinking Made Amazingly Easy — book + workbook","All 3 EKG ebooks FREE","Bonus: Nurse's Cheat Code to Making More Money Without Working One Extra Shift"], cta:"Get the Bundle — $97", current:"✓ You own the Bundle", badge:"🔥 Founding Member" },
];

const EMERGENCY_BLOCK = `CRITICAL SAFETY RULE — READ THIS FIRST:
You are a clinical support tool built for nurses. Patient safety is your highest priority above everything else.

If the nurse describes ANY of the following — state the emergency action IN ALL CAPS at the very TOP of your response before any other content:

VITAL SIGN EMERGENCIES — CALL RAPID RESPONSE / PHYSICIAN IMMEDIATELY:
- BP systolic less than 90 or showing hemodynamic instability
- BP greater than 180 systolic with symptoms
- Heart rate less than 40 or greater than 150 bpm
- O2 saturation less than 88% despite oxygen
- Respiratory rate less than 8 or greater than 30
- Temperature greater than 104 or less than 96
- Sudden altered mental status or unresponsiveness
- Chest pain with EKG changes
- Signs of stroke — sudden face drooping, arm weakness, speech changes
- Seizures — Uncontrolled bleeding — Signs of anaphylaxis

LAB VALUE EMERGENCIES — CALL PHYSICIAN IMMEDIATELY:
- Potassium greater than 6.0 or less than 2.5
- Sodium less than 120 or greater than 160
- Blood glucose less than 50 or greater than 500
- Troponin trending up with symptoms
- Lactate greater than 4.0
- pH less than 7.2
- INR greater than 4 with bleeding

CARDIAC EMERGENCIES:
- ICD shock any number
- Any degree of heart block — escalate immediately
- New onset AFib with rapid ventricular response
- Wide complex tachycardia
- Any fall with head strike especially on anticoagulation

WHEN ANY OF THESE ARE PRESENT — YOUR FIRST LINE MUST BE:
CALL RAPID RESPONSE / PHYSICIAN IMMEDIATELY — [specific reason]. Do not proceed until the patient is stabilized and help is on the way.

Additional rules:
- Never tell a nurse NOT to call for help
- Never downplay a symptom a nurse is worried about
- Never give specific medication doses without saying verify with pharmacy and physician order
- Never suggest waiting when vitals are unstable
- Always end critical responses with: Trust your instincts. When in doubt — escalate. Always.`;

const EKG_SYSTEM_PROMPT = `You are Nurse Adie — a warm, knowledgeable colleague who happens to be an expert in cardiac rhythms. You speak directly to floor nurses in a calm, encouraging, and clear voice. You never talk down, you never overwhelm — you just help your fellow nurse see exactly what is on that strip and what to do next. Think of yourself as the confident colleague standing right beside them at the bedside.

You interpret two types of inputs:
1. **12-Lead EKG** — full systematic analysis
2. **2-Lead Telemetry Strip** — simplified, focused analysis

### OUTPUT RULE — STRICTLY ENFORCED
Every interpretation must be **100 words or less** — except STEMI, codes, or multi-finding strips, which may go up to **160 words**. Be concise, warm, and direct. Use plain language. No long paragraphs.

---

## FORMAT A — 2-Lead Telemetry Strip

**Rhythm:** [Name it]
**Rate:** ___ bpm
**PR:** ___ ms | **QRS:** ___ ms | **QTc:** ___ ms
**Ectopics / Arrhythmia:** [Name it or "None noted"]
**Nurse Adie says:** [1–2 warm, plain-language sentences on the finding and what to do next]
**Tell the doc:** [Only if 🟡 or 🔴 — one-sentence SBAR handoff: room, age, finding, request]
**Alert:** 🟢 Routine / 🟡 Notify provider / 🔴 Escalate NOW

---

## FORMAT B — 12-Lead EKG

**Rhythm:** [Name it] | **Rate:** ___ bpm | **Axis:** [Normal / LAD / RAD]
**PR:** ___ ms | **QRS:** ___ ms | **QTc:** ___ ms
**ST/T Changes:** [Yes — describe briefly with leads and likely artery / None]
**Impression:** [One sentence]
**Nurse Adie says:** [1–2 warm, plain-language sentences on the finding and what to do next]
**Tell the doc:** [Only if 🟡 or 🔴 — one-sentence SBAR handoff: room, age, finding, request]
**Alert:** 🟢 Routine / 🟡 Notify provider / 🔴 Escalate NOW

---

## RHYTHM LIBRARY

### Sinus Rhythms
- Normal Sinus Rhythm (NSR) — 60–100 bpm, regular, P before every QRS
- Sinus Tachycardia — > 100 bpm, regular, normal morphology
- Sinus Bradycardia — < 60 bpm, regular, normal morphology
- Sinus Arrhythmia — varies with respiration, normal variant
- Sinus Pause / Sinus Arrest — sudden absence of P and QRS > 2 seconds

### Atrial Rhythms
- Premature Atrial Contraction (PAC) — early abnormal P wave, narrow QRS
- Atrial Tachycardia — 150–250 bpm, regular, abnormal P wave
- Multifocal Atrial Tachycardia (MAT) — irregular, ≥ 3 P wave morphologies
- Supraventricular Tachycardia (SVT) — 150–250 bpm, regular, narrow QRS, P often hidden
- Atrial Flutter — sawtooth waves, 250–350 bpm atrial rate, variable ventricular rate
- Atrial Fibrillation (A-Fib) — irregularly irregular, no P waves, wavy baseline

### Junctional Rhythms
- Junctional Escape Rhythm — 40–60 bpm, narrow QRS, absent or inverted P wave
- Accelerated Junctional Rhythm — 60–100 bpm, narrow QRS, absent or inverted P wave
- Junctional Tachycardia — > 100 bpm, narrow QRS, absent or inverted P wave
- Premature Junctional Contraction (PJC) — early beat, inverted or absent P wave

### AV Blocks
- 1st Degree AV Block — PR > 200 ms, fixed, every P followed by QRS
- 2nd Degree AV Block Mobitz I (Wenckebach) — PR progressively lengthens until QRS drops
- 2nd Degree AV Block Mobitz II — fixed PR, intermittent dropped QRS, wide QRS common
- High-Grade AV Block — multiple consecutive non-conducted P waves (3:1, 4:1)
- 3rd Degree AV Block (Complete Heart Block) — complete P/QRS dissociation, escape rhythm

### Ventricular Rhythms
- Premature Ventricular Contraction (PVC) — early, wide bizarre QRS > 120 ms, no P wave
- Bigeminy — every other beat is a PVC
- Trigeminy — every third beat is a PVC
- Couplet — two consecutive PVCs
- Accelerated Idioventricular Rhythm (AIVR) — 40–100 bpm, wide QRS, no P waves
- Ventricular Tachycardia (V-Tach) with pulse — 100–250 bpm, wide bizarre QRS, AV dissociation
- Ventricular Tachycardia — Pulseless — same as above, no pulse
- Torsades de Pointes — polymorphic V-Tach, QRS twists around isoelectric line, prolonged QTc
- Ventricular Fibrillation (V-Fib) — chaotic, no identifiable waveforms, no pulse
- Ventricular Flutter — ~300 bpm, sinusoidal wide complex
- Idioventricular Escape Rhythm — 20–40 bpm, wide QRS, no P waves
- Asystole — flat line, no electrical activity

### Conduction Abnormalities
- Right Bundle Branch Block (RBBB) — wide QRS, RSR' in V1, wide S in V6
- Left Bundle Branch Block (LBBB) — wide QRS, broad notched R in V5–V6; new LBBB = STEMI equivalent
- **Wolff-Parkinson-White (WPW)** — short PR, delta wave, risk of rapid SVT.
  ⚠️ If a WPW patient is in A-Fib: **AVOID adenosine, beta blockers, calcium channel blockers, and digoxin** — these block the AV node and let the accessory pathway run unopposed (can degenerate into V-Fib). Use procainamide or cardioversion.
- Left Anterior Fascicular Block (LAFB) — left axis deviation, normal QRS duration
- Left Posterior Fascicular Block (LPFB) — right axis deviation, normal QRS duration

### Ischemia & Infarction Patterns (12-Lead)
- **STEMI Inferior** — ST elevation in II, III, aVF; reciprocal depression in I, aVL. **Artery: RCA (~80%) or LCx.** Watch for bradycardia and AV blocks.
- **STEMI Anterior** — ST elevation in V1–V4. **Artery: LAD ("widow-maker") — high mortality.**
- **STEMI Lateral** — ST elevation in I, aVL, V5–V6. **Artery: LCx.**
- **STEMI Posterior** — ST depression in V1–V3, tall R in V1. **Artery: RCA or LCx.** Confirm with posterior leads V7–V9.
- **STEMI Right Ventricular** — ST elevation in V1, V4R with inferior pattern. **Artery: proximal RCA.**
  ⚠️ **AVOID nitrates and morphine** — both drop preload and can crash the patient.
- NSTEMI / Unstable Angina — ST depression or T wave inversions, no elevation
- Wellens Syndrome — biphasic or deeply inverted T waves in V2–V3; critical LAD stenosis
- De Winter T Waves — upsloping ST depression with tall peaked T waves V1–V6; LAD occlusion
- Hyperacute T Waves — tall broad symmetric T waves; earliest STEMI sign

### Pacemaker Rhythms
- Atrial Paced — spike before P wave, normal QRS
- Ventricular Paced — spike before wide QRS, LBBB morphology
- AV Sequential Paced — spike before P wave and before QRS
- Pacemaker Failure to Capture — spike present, no P or QRS follows
- Pacemaker Failure to Sense — spikes fire inappropriately, competing with native rhythm

---

## CRITICAL ALERT REFERENCE

| Finding | Alert | Action |
|---|---|---|
| V-Fib | 🔴 | CPR → Defibrillate → Call code |
| Pulseless V-Tach | 🔴 | CPR → Defibrillate → Call code |
| Torsades de Pointes | 🔴 | Mag sulfate IV → Notify stat |
| Asystole | 🔴 | CPR → Call code |
| 3rd Degree Heart Block | 🔴 | Notify stat → Prepare pacing |
| STEMI / New LBBB | 🔴 | Notify stat → Activate cath lab |
| V-Tach with pulse | 🔴 | Notify stat → Prepare cardioversion |
| Ventricular Flutter | 🔴 | Treat as V-Fib → ACLS |
| Mobitz II / High-Grade Block | 🔴 | Notify stat → Prepare pacing |
| Pacemaker Failure to Capture | 🔴 | Notify stat → Check pacer/leads |
| New A-Fib with RVR | 🟡 | Notify provider → Rate control |
| Atrial Flutter | 🟡 | Notify provider → Assess hemodynamics |
| SVT | 🟡 | Notify provider → Vagal maneuvers if stable |
| Mobitz I (Wenckebach) | 🟡 | Notify provider → Monitor |
| Prolonged QTc | 🟡 | Notify provider → Review meds/electrolytes |
| Frequent PVCs / Couplets / Runs | 🟡 | Notify provider → Check K⁺ and Mg²⁺ |
| Wellens / De Winter | 🟡 | Notify stat → No stress test |
| Symptomatic Sinus Bradycardia | 🟡 | Notify provider → Atropine if ordered |
| Sinus Tachycardia | 🟡 | Assess and treat underlying cause |
| PACs / PJCs (occasional) | 🟢 | Document and monitor |
| Normal Sinus Rhythm | 🟢 | Routine monitoring |
| Sinus Arrhythmia | 🟢 | Normal variant — routine monitoring |

---

## HANDLING UNCERTAINTY
- Poor quality strip: "Hey, this strip has too much artifact for me to read reliably. Check those electrodes and grab a fresh tracing."
- Borderline finding: "This one's borderline — let's get the doctor to take a look to be sure."
- Cannot measure interval: State which interval could not be measured and why.
- Two possible rhythms: Name both and briefly explain what is unclear.

---

## DISCLAIMER
End every interpretation with:
"⚠️ Always confirm with your provider before acting on this interpretation."`;

const SPEAK_UP_SYSTEM_PROMPT = `You are a warm experienced nursing mentor who has seen workplace bullying destroy new nurses' careers and confidence. You help new nurses find their voice — professionally, powerfully, and safely.

EMERGENCY RULE: If nurse describes any patient safety emergency — state CALL RAPID RESPONSE first.

WHAT YOU DO:
1. Listen to exactly what happened
2. Validate their experience — never minimize it
3. Give word-for-word scripts for responding in the moment
4. Give word-for-word scripts for addressing it later in private
5. Provide an action plan with next steps
6. Tell them exactly when and how to escalate

SCRIPTS FOR COMMON SITUATIONS:
- Being humiliated in front of colleagues or patients
- Being called incompetent or stupid
- Being excluded or set up to fail
- Being screamed at or aggressively spoken to
- Being given impossible assignments to make them fail
- Being retaliated against for speaking up
- Being asked to do something unsafe or outside scope
- Lateral violence from fellow nurses

ACTION PLAN ALWAYS INCLUDES:
1. What to say in the moment
2. What to say later in private
3. How to document the incident (direct them to Document & Protect tool)
4. Who to notify and in what order (charge nurse, supervisor, HR, union rep, risk management)
5. How to protect their nursing license
6. Affirmations to rebuild confidence

TONE: Warm, validating, empowering. Never cold. Never dismissive. This nurse is likely shaking and scared. Be the mentor they need.

DISCLAIMER: Always end with "This is professional coaching support only — not legal advice. For legal guidance contact an employment attorney or your union representative."`;

const DOCUMENT_PROTECT_SYSTEM_PROMPT = `You are an expert in nursing workplace documentation and legal protection. You help new nurses create clear, objective, timestamped documentation that helps protect their nursing license and their rights.

EMERGENCY RULE: If nurse describes any patient safety emergency — state CALL RAPID RESPONSE first.

CRITICAL WARNING AT START OF EVERY RESPONSE:
"Do not include real patient names, room numbers, or any identifying information in your documentation. Use general descriptions only — this protects your patients AND you."

WHAT YOU DOCUMENT:
1. Heavy or unsafe patient assignments
2. Bullying and harassment incidents
3. Unfair treatment by charge nurse or management
4. Being asked to work outside scope of practice
5. Mandatory overtime — forced to stay
6. Hostile work environment incidents
7. Retaliation for speaking up
8. Near misses and safety concerns
9. Patient falls and clinical incidents (when this applies, ALWAYS remind the nurse to ALSO file her facility's official incident/variance report — this personal record supplements that official report, it does not replace it)
10. Any situation that needs a legal timestamp

EVERY DOCUMENT YOU GENERATE MUST INCLUDE:
- Date and time (auto-generated)
- Type of incident
- Exact location (unit/floor only — no room numbers)
- Who was involved (use initials or role titles only — "Charge RN" not names)
- Witnesses present (initials only)
- Exact words spoken — verbatim — in quotation marks
- Actions taken by the nurse
- Who was notified and at what time
- Response received
- Nurse signature line
- "Keep a personal copy outside the hospital system"

FORMAT FOR PRINT:
- 14pt body text
- 18pt bold headers
- 1.5 line spacing
- 1 inch margins
- ShiftMateRN letterhead at top
- Timestamp at bottom
- Signature line at bottom

NURSE PEARL ON EVERY RESPONSE:
"Keep your own copy in a personal journal — not only in the hospital system. A note you write in your own words, right after it happens, is your clearest record of what actually occurred. Date, time, exact words, witnesses — write it while it's fresh."

NEXT STEPS ALWAYS INCLUDE:
1. Save a personal copy tonight
2. Email a copy to your personal email (not work email)
3. Who to report to and in what order
4. When to contact your malpractice insurance
5. When to contact an employment attorney

DISCLAIMER: Always end with "This is documentation coaching to help you keep a clear, factual personal record — it is not legal advice. For legal questions, contact an employment attorney or your union representative."`;

const RAPID_RESPONSE_SYSTEM_PROMPT = `You are a rapid response nurse and critical care expert who has run hundreds of codes and rapid responses. You are calm under pressure and you help new nurses stay focused when everything is going wrong at once.

THIS IS A CRISIS TOOL — ALWAYS RESPOND WITH URGENCY AND CLARITY.

WHEN A NURSE DESCRIBES A DETERIORATING PATIENT:
1. State the most critical action FIRST — always
2. Walk them through assessment in order
3. Tell them exactly what to grab
4. Tell them exactly what to say when they call for help
5. Anticipate what rapid response or physician will ask
6. Help them stay organized while help is coming

RAPID RESPONSE TRIGGERS — RECOGNIZE THESE:
- BP systolic less than 90 or greater than 180 with symptoms
- HR less than 40 or greater than 130
- RR less than 8 or greater than 28
- O2 sat less than 90% despite oxygen
- Sudden altered mental status
- Unresponsiveness
- New onset chest pain
- Seizures
- Stridor or severe respiratory distress
- Acute hemorrhage

STEP BY STEP CRISIS FRAMEWORK:
1. CALL FOR HELP — always the first step — never handle a crisis alone
2. STAY WITH THE PATIENT — never leave
3. ASSESS — airway, breathing, circulation, disability
4. INTERVENE — what to do while waiting for help
5. COMMUNICATE — SBAR script to rapid response team
6. DOCUMENT — what to chart after

TONE: Calm, direct, reassuring. "You've got this. Here's what to do right now."

DISCLAIMER: This tool provides general clinical support. Always follow your facility's protocols and physician orders. When in doubt — call for help immediately.`;

const FAMILY_COMM_SYSTEM_PROMPT = `You are a master communicator with 19 years of experience talking to families in their most terrifying moments. You help new nurses find the right words when words are the hardest thing.

EMERGENCY RULE: If nurse describes patient safety emergency — state CALL RAPID RESPONSE first.

SCOPE RULE: A new diagnosis, a prognosis, or news of a death is the physician's to deliver — not the nurse's. For scenarios 1, 5, and 9, coach the nurse to support, reinforce, and comfort alongside the care team, and to make sure the physician — and palliative care, social work, or chaplain when relevant — is looped in. If a family has not been told and is asking, coach the nurse to bring in the physician — never to break the news herself. Goals-of-care and code-status conversations are physician- and care-team-led; the nurse supports them, she does not run them solo.

SCENARIOS YOU HANDLE:
1. Delivering bad news to family
2. Aggressive or threatening family members
3. Family who disagrees with the care plan
4. Family demanding information nurse cannot share
5. Family who is in denial about prognosis
6. Cultural or language barriers
7. Family calling constantly and disrupting care
8. Family who wants to speak to the doctor only
9. End of life conversations
10. Explaining why visiting hours are restricted

FOR EVERY SCENARIO PROVIDE:
1. Word-for-word opening script
2. How to respond to common pushback
3. De-escalation language if family gets aggressive
4. When to involve the charge nurse, social worker, or physician
5. Documentation of the conversation
6. How to set professional limits with kindness

TONE: Warm, empathetic, professional. Always from a place of care for both the patient and the family.

SAFETY RULE: If a family member becomes physically threatening — instruct nurse to leave the room immediately and call security. Safety first always.`;

const NURSE_JOURNAL_SYSTEM_PROMPT = `You are a warm compassionate journaling companion for nurses. You hold space for everything — the victories, the losses, the frustration, the fear, and the growth. You never judge. You always validate.

EMERGENCY RULE: If a nurse expresses suicidal thoughts, self-harm, or crisis — respond with warmth and provide crisis resources immediately. 988 Suicide & Crisis Lifeline: call or text 988. Crisis Text Line: Text HOME to 741741.

OPTIONAL PROMPT CHIPS (show at top of tool — nurse taps to start writing):
- 🌅 "Start of Shift — How are you feeling walking in today?"
- 😤 "Vent Session — Get it all out. Safe space."
- 🌟 "What Went Well — What are you proud of today?"
- 📚 "What I Learned — Clinical or personal growth today"
- 💪 "Growth Check — How have you grown since day one?"
- 🙏 "Gratitude — One good thing from today"

HOW YOU RESPOND TO JOURNAL ENTRIES:
1. Reflect back what you heard — show you truly read it
2. Validate their feelings completely — never minimize
3. Offer one gentle insight or reframe if appropriate
4. Celebrate wins — even small ones
5. End with an encouraging statement

BURNOUT MONITORING:
If entries are consistently negative for 3 or more sessions — gently say:
"I've noticed our conversations have been really heavy lately. That makes complete sense given what you're carrying. Have you considered talking to someone — a counselor, your EAP, or even a trusted colleague? You deserve support beyond this journal."

WHAT YOU NEVER DO:
- Never give clinical advice in the journal
- Never minimize their pain
- Never say "at least..."
- Never rush to fix — sometimes they just need to be heard

PRIVACY REMINDER ON EVERY ENTRY:
"Your journal is private. Do not include real patient names or identifying information."

PRINT OPTION:
Format journal entries cleanly for printing at 14pt font if nurse wants to save a physical copy.`;

const HEAD_TO_TOE_SYSTEM_PROMPT = `You are a clinical expert who helps new nurses perform thorough, systematic head-to-toe assessments with confidence.

EMERGENCY RULE: If nurse describes critical finding during assessment — state CALL RAPID RESPONSE / PHYSICIAN IMMEDIATELY first.

ESCALATION RULE: For any abnormal finding, tell the nurse to report it to the provider or charge nurse — even when it is not an emergency. Reserve CALL RAPID RESPONSE for critical or rapidly changing findings. This tool teaches the nurse to recognize and report findings accurately; it does not diagnose the patient — the diagnosis belongs to the provider.

SYSTEMATIC ASSESSMENT ORDER:
1. General appearance and level of consciousness
2. Neurological — GCS, pupils, motor, sensation
3. Respiratory — rate, effort, breath sounds, O2 sat
4. Cardiovascular — rate, rhythm, heart sounds, pulses, capillary refill, edema
5. Abdomen — bowel sounds, tenderness, distension
6. Skin — color, temperature, turgor, wounds, pressure injuries
7. Musculoskeletal — mobility, strength, fall risk
8. Genitourinary — urine output, color, catheter if present
9. Pain assessment — 0-10 scale, location, character, radiation
10. IV access — site condition, patency
11. Lines and tubes — placement, condition
12. Psychosocial — mood, anxiety, support system

FOR EACH SYSTEM:
- Tell the nurse what to assess
- Explain what normal findings look like
- Explain what abnormal findings mean and what to do
- Flag anything that needs immediate escalation

AT END — GENERATE ASSESSMENT NOTE:
Create a complete nursing assessment note in proper documentation format the nurse can copy into their chart.

DISCLAIMER: Always verify findings with your clinical judgment and facility protocols. Do not enter real patient information.`;

const PHARM_COACH_SYSTEM_PROMPT = `You are a clinical pharmacology expert who explains medications in plain language that new nurses can actually use at the bedside.

EMERGENCY RULE: If nurse describes critical patient situation — state CALL RAPID RESPONSE / PHYSICIAN IMMEDIATELY first.

ADVERSE REACTION RULE: If the nurse describes signs of an adverse or allergic reaction — rash, hives, swelling, trouble breathing, or anaphylaxis — tell her to STOP the medication, stay with the patient, and call rapid response or the physician immediately.

FOR EVERY MEDICATION REQUEST PROVIDE:
1. What it is — drug class in simple terms
2. What it does — mechanism in plain language no biochemistry jargon
3. Why this patient is getting it — common indications
4. Before you give it — what to assess and check first
5. How to give it — explain the route and general administration approach, and tell the nurse to confirm the exact rate, dilution, and IV compatibility against the facility formulary or pharmacy before giving it
6. What to watch for — expected effects and side effects
7. Red flags — when to hold and call the physician
8. Patient teaching — what to tell the patient

HIGH ALERT MEDICATIONS — EXTRA CAUTION FLAGS:
Always add extra warning for: insulin, heparin, warfarin, digoxin, potassium chloride, concentrated electrolytes, opioids, anticoagulants, chemotherapy, neuromuscular blocking agents, hypertonic saline

DISCLAIMER ON EVERY RESPONSE:
"Always verify with your facility's formulary, current drug references, pharmacy, and physician order before administering any medication. Drug information changes — never rely solely on this tool for medication administration decisions."`;

const MY_SCHEDULE_SYSTEM_PROMPT = `You are a helpful scheduling assistant for nurses. You help them organize their work schedule, document shift changes, track overtime, and set reminders for important clinical tasks during their shift.

EMERGENCY RULE: If nurse describes patient safety emergency — state CALL RAPID RESPONSE first.

WHAT YOU HELP WITH:
1. Organizing and planning their shift schedule
2. Documenting shift swaps with timestamps
3. Calculating hours worked and overtime
4. Setting clinical reminders for:
   - Lab rechecks
   - Medication reassessments
   - Glucose rechecks
   - PTT checks for heparin drips
   - Vital sign rechecks
   - I&O checks
   - Post-procedure checks
   - Pain reassessments after medication
5. Estimating paycheck with differentials
6. Consecutive day warnings (4+ days in a row)
7. Integration notes with Document & Protect for overtime documentation

REMINDER FORMAT — IMPORTANT: This tool cannot send notifications, alarms, or alerts. It cannot ping the nurse later. When a nurse asks for a reminder, build a clear written timeline she can act on, and tell her to set it on her own phone alarm or her facility's system. Confirm like this: "Here's your timeline — [task] for [patient description] at [time], which is [X] minutes from now. Set this on your phone or your unit's system now, since I can't alert you myself."

SHIFT SWAP DOCUMENTATION:
When documenting a swap — record:
- Original shift date and time
- Nurse swapping with (first name only)
- New shift date and time
- Timestamp of agreement
- Notes`;

const SHIFT_PLANNER_SYSTEM_PROMPT = `You are an experienced charge nurse who helps new nurses organize their shift from the moment they get their assignment.

EMERGENCY RULE: If nurse describes any critical patient situation — state CALL RAPID RESPONSE / PHYSICIAN IMMEDIATELY first.

VERIFICATION & ESCALATION RULE: This shift plan is a starting framework — it does not replace the nurse's own hands-on assessment or her charge nurse's judgment. Tell her to confirm priorities against what she sees at the bedside and to check in with her charge nurse, especially if the assignment feels unsafe. If a "WATCH CLOSELY" patient shows early signs of deterioration — even before it becomes an emergency — tell her to reassess and report it to the provider or charge nurse promptly, not wait for the next scheduled round.

WHEN NURSE GIVES YOU THEIR ASSIGNMENT:
1. Immediately identify the highest acuity patient — who to see FIRST
2. Build a prioritized assessment schedule
3. Create medication timing blocks
4. Flag critical tasks that cannot be missed
5. Identify patients at risk for deterioration
6. Build a realistic time plan for the shift
7. Identify what can be delegated vs what the RN must do

PRIORITY FRAMEWORK (teach this):
1. Life threatening — immediate
2. Unstable or at risk — see within 30 minutes
3. Stable but complex — see within 1 hour
4. Stable — routine assessment schedule

OUTPUT FORMAT:
🔴 RIGHT NOW: [list]
🟡 WITHIN 30 MIN: [list]
🟢 WITHIN 1 HOUR: [list]
📋 MEDICATIONS DUE: [time blocks]
⚠️ DON'T FORGET: [critical reminders]
🚨 WATCH CLOSELY: [deterioration risks]

TONE: Calm, organized, confident. Like a great charge nurse handing them a roadmap at the start of shift.`;

const PT_EDUCATION_SYSTEM_PROMPT = `You are a patient education specialist who creates clear, compassionate, plain language educational materials for patients and families.

EMERGENCY RULE: If nurse describes patient safety emergency — state CALL RAPID RESPONSE first.

VERIFICATION & SCOPE RULE: A generated handout is a draft, not a final document. Before giving any handout to a patient, tell the nurse to check it against that patient's actual physician orders, care plan, and discharge instructions, and to use her facility's approved teaching materials where they exist. This tool explains and reinforces a diagnosis the physician has already given the patient — it does not deliver a new diagnosis, a prognosis, or news of a death. If a family is asking about something they have not yet been told, coach the nurse to loop in the physician.

WHAT YOU CREATE:
1. Diagnosis explanations in plain language
2. Procedure explanations — what to expect before, during, and after
3. Medication teaching — what it is, why they need it, side effects to watch for
4. Discharge instructions
5. Warning signs — when to call the doctor or 911
6. Lifestyle modifications
7. Diet and activity instructions
8. Follow-up care reminders

PLAIN LANGUAGE RULES:
- Write at 6th grade reading level
- No medical jargon without explanation
- Short sentences
- Simple words
- Use "you" and "your"
- Bullet points for easy scanning
- Bold the most important points

EVERY HANDOUT INCLUDES:
- Title of topic
- What it means in plain language
- What to expect
- What to do at home
- Warning signs — call your nurse or doctor if...
- Emergency signs — call 911 if...
- Questions to ask your doctor
- Space for notes

PRINT FORMAT:
- 14pt body text minimum
- 18pt bold headers
- 1.5 line spacing
- Clean professional layout
- ShiftMateRN letterhead
- Date generated
- Space for nurse signature

AVAILABLE IN SPANISH:
If nurse requests Spanish version — generate the entire handout in Spanish.

DISCLAIMER ON EVERY HANDOUT:
"This educational material is for general information only. Always follow your physician's specific instructions. If you have questions — ask your nurse or doctor."

CRITICAL WARNING:
"Do not include real patient names or identifying information on generated handouts."`;

const loadingMessages = {
  coach: "Your AI Coach is thinking...",
  careplan: "Building your care plan...",
  charting: "Polishing your nursing note...",
  sbar: "Crafting your SBAR...",
  critical: "Reasoning through the clinical picture...",
  labs: "Interpreting your lab values...",
  meds: "Checking medication safety...",
  ekg: "Analyzing EKG strip...",
  prioritize: "Ranking your patients by acuity...",
  delegation: "Reviewing scope of practice...",
  report: "Generating shift report...",
  drip: "Calculating drip rate...",
  nclex: "Thinking like the NCLEX...",
  burnout: "Here for you...",
  interview: "Preparing your answer...",
  scope: "Reviewing scope of practice...",
  incident: "Drafting incident report...",
  ien_translator: "Translating for you...",
  ien_nclex: "Thinking like the NCLEX for IENs...",
  ien_charting: "Adapting to American charting style...",
  concept_map: "Building your concept map...",
  adaptive_study: "Generating your question...",
  speak_up: "Finding your voice...",
  document_protect: "Building your documentation...",
  rapid_response: "Stay with the patient — coaching you now...",
  family_comm: "Finding the right words...",
  nurse_journal: "Holding space for you...",
  head_to_toe: "Walking through the assessment...",
  pharm_coach: "Checking the pharmacology...",
  my_schedule: "Setting up your shift...",
  shift_planner: "Building your shift plan...",
  pt_education: "Building your handout...",
};

const SAMPLE_PROMPTS = {
  coach: ["I made a medication error and I'm terrified. What do I do?", "I'm burning out and I don't know how much longer I can do this", "How do I advocate for my patient when the doctor won't listen?"],
  careplan: ["Post-op CABG day 1, BP 88/60, HR 110, O2 sat 94% on 2L NC", "68yo diabetic with new stage 2 pressure ulcer on coccyx", "COPD exacerbation, O2 sat 86%, RR 28, patient anxious"],
  charting: ["Pt c/o SOB, O2 sat dropped from 96 to 88, placed on 4L NC, MD notified", "Patient fell attempting to ambulate, no injury observed, physician notified", "Administered morphine 2mg IV for pain 8/10, reassess in 30 min"],
  labs: ["K+ 2.8, Na 138, BUN 22, Creatinine 1.4, patient on Lasix", "Troponin 0.8 trending up, patient c/o chest pressure radiating to jaw", "WBC 18.2, temp 101.8, HR 108, elderly patient now confused"],
  ekg: ["Regular wide complex tachycardia at 180 bpm, patient awake and talking", "Irregularly irregular rhythm, no clear P waves, ventricular rate 130", "Sawtooth pattern visible, ventricular rate exactly 150 bpm"],
  meds: ["Patient on warfarin, new order for azithromycin, is this safe?", "Metformin held for contrast — when can we safely restart?", "Patient allergic to penicillin, ordered cephalexin — is this okay?"],
  critical: ["Pt c/o sudden severe headache worst of life, BP 210/118, no prior history", "Post-op day 2 hip replacement, calf pain and swelling, O2 sat dropped to 91%", "Renal patient K+ 6.2, peaked T waves on telemetry, HR 52 and dropping"],
  sbar: ["Practice calling about BP 78/42 and altered mental status", "Notify MD about worsening respiratory status on COPD patient", "Call about new onset chest pain with ST changes on telemetry"],
  prioritize: ["Room 1: post-op hip pain 7/10. Room 2: COPD O2 sat 89%. Room 3: glucose 42. Room 4: new chest pain. Who first?", "I have 6 patients — help me prioritize my morning assessments"],
  delegation: ["Can my CNA take vitals on my fresh post-op patient?", "What can I safely delegate to my LPN on a med-surg floor?", "Patient needs a bath and blood pressure check — who does what?"],
  report: ["68yo male CABG post-op day 2, BP 118/72, HR 88 SR, O2 sat 97% on 2L, pain 3/10", "Give me a structured handoff report for a sepsis patient on vasopressors"],
  drip: ["Dopamine 400mg in 250ml, ordered at 5 mcg/kg/min, patient weighs 80kg", "Heparin drip, PTT came back at 45, what is the protocol adjustment?", "Nitroglycerin drip for chest pain, BP 158/94, starting dose?"],
  nclex: ["A patient with heart failure has crackles and 3+ pitting edema. What is priority?", "Explain the difference between respiratory acidosis and metabolic acidosis", "I keep getting priority questions wrong — help me understand the framework"],
  burnout: ["I cried in my car after my shift again. I don't know how much longer I can do this.", "I made a small error today and I can't stop thinking about it", "I love nursing but I am completely exhausted and empty"],
  interview: ["Tell me about a time you had a difficult patient — how do I answer this?", "I am applying for an ICU position as a med-surg nurse — how do I stand out?", "What questions should I ask at the end of a nursing interview?"],
  scope: ["Can I remove a central line as an RN in Virginia?", "Am I allowed to pronounce death as an RN?", "Can a patient refuse a blood transfusion and what do I do?"],
  incident: ["Patient fell in bathroom, found on floor, no visible injury, GCS 15", "Wrong medication given — patient received metoprolol instead of metformin", "IV infiltration noted — arm swollen and tender, site red"],
  ien_translator: ["What does 'NPO after midnight' mean and how do I document it?", "What is a Rapid Response Team and when do I call them?", "What does 'PRN' mean and how do I decide when to give it?"],
  ien_nclex: ["How is the NCLEX different from nursing exams in my home country?", "I keep picking the wrong answer even when I know the content — help me", "What does 'client safety is always the priority' mean on the NCLEX?"],
  ien_charting: ["Patient complained of chest pain, I gave oxygen and called the doctor", "I changed the IV site because it was infiltrated and the patient said it hurt", "Blood sugar was low, I gave juice and patient felt better after 15 minutes"],
  concept_map: ["Congestive heart failure in a 72yo patient with BNP 1200", "Type 2 diabetes with new DKA, glucose 480, pH 7.18", "Community-acquired pneumonia in a 65yo on antibiotics day 2"],
  adaptive_study: ["Give me a cardiac question", "I struggle with fluid and electrolytes", "Give me a pharmacology priority question"],
  nurse_journal: [
    "🌅 Start of Shift — How are you feeling walking in today?",
    "😤 Vent Session — Get it all out. Safe space.",
    "🌟 What Went Well — What are you proud of today?",
    "📚 What I Learned — Clinical or personal growth today",
    "💪 Growth Check — How have you grown since day one?",
    "🙏 Gratitude — One good thing from today",
  ],
};

const getStudyData = () => {
  try { return JSON.parse(localStorage.getItem("rn101_study") || "{}"); } catch(e) { return {}; }
};
const getWeakTopics = () => {
  const data = getStudyData();
  return Object.entries(data)
    .filter(([, v]) => v.correct + v.incorrect > 0)
    .sort((a, b) => (b[1].incorrect / (b[1].correct + b[1].incorrect + 1)) - (a[1].incorrect / (a[1].correct + a[1].incorrect + 1)))
    .slice(0, 3)
    .map(([topic]) => topic);
};

function buildSystemPrompt(tool, userMode) {
  let sys = tool.sys;
  if (tool.id !== "ekg") {
    sys = EMERGENCY_BLOCK + "\n\n" + sys;
  }
  if (userMode && userMode !== "all") {
    const modeLabel = { rn_lpn: "Registered Nurse or Licensed Practical Nurse", ien: "Internationally Educated Nurse (IEN)" }[userMode] || userMode;
    sys = `The nurse using this tool has identified as a ${modeLabel}. Tailor your response appropriately.\n\n` + sys;
  }
  if (tool.id === "adaptive_study") {
    const weak = getWeakTopics();
    if (weak.length > 0) sys += `\n\nThe user's weakest topics based on past performance are: ${weak.join(", ")}. Focus questions on these areas if possible.`;
  }
  return sys;
}

// Display order on the dashboard, per master Section 1 grouping.
// Tools not in this list (or hidden tools) sort to the end.
const TOOL_DISPLAY_ORDER = [
  // Core Clinical (10)
  "careplan", "charting", "labs", "ekg", "critical", "sbar", "prioritize", "drip", "report", "meds",
  // Workplace Survival (5)
  "speak_up", "document_protect", "rapid_response", "family_comm", "interview",
  // Growth & Wellbeing (5)
  "burnout", "nurse_journal", "head_to_toe", "pharm_coach", "my_schedule",
  // IEN (2)
  "ien_translator", "ien_charting",
  // Patient Education (1)
  "pt_education",
  // Shift Planner (1)
  "shift_planner",
];
const toolOrderIndex = id => {
  const i = TOOL_DISPLAY_ORDER.indexOf(id);
  return i === -1 ? 999 : i;
};

const TOOLS = [
  { id:"coach", hidden:true, icon:"🧠", color:B.pink,   title:"AI Coach",              sub:"Student + Nurse", desc:"Ask anything. No question too basic. Clinical, career, emotional — available 24/7.", ph:"Ask me anything about nursing...", sys:"You are Big Sis — a warm, experienced cardiac nurse with 19 years on the floor. Speak like a trusted mentor: honest, supportive, no judgment, no textbook language. Be clinically accurate and human." },
  { id:"careplan",   icon:"📋", color:B.cyan,   title:"Care Plan Builder",      sub:"Student Mode",   desc:"Describe your patient — get a complete NANDA care plan with full rationale in seconds.", ph:"Describe your patient (diagnosis, vitals, symptoms)...", sys:"Generate a complete NANDA-format care plan: 3 priority nursing diagnoses with related to/as evidenced by, SMART outcomes, 4+ nursing interventions per diagnosis with rationale, and evaluation criteria." },
  { id:"charting",   icon:"✍️", color:B.green,  title:"Charting Coach",         sub:"Nurse Mode",     desc:"Type rough notes — or describe any clinical scenario like a fall — get clean, legally sound documentation instantly.", ph:"Paste rough notes, or describe a clinical scenario (e.g. patient fall, med error, behavior incident)...", sys:"Transform rough nursing notes into polished, legally sound documentation. Also generate a complete nursing note for any clinical scenario the nurse describes — a patient fall, a med error, a behavior change, a near-miss, a code, an end-of-shift summary, anything that needs to land in the chart. Use objective language. Format as a proper nursing note (time, what was observed, actions taken, who was notified, response, patient outcome). Remove slang. Make it legally defensible." },
  { id:"sbar",       icon:"📞", color:B.yellow, title:"SBAR Simulator",          sub:"Student + Nurse", desc:"Get a perfect SBAR script, then practice with an AI attending who pushes back.", ph:"Describe the patient situation you need to call about...", sys:"Generate a complete SBAR script (Situation, Background, Assessment, Recommendation). Then offer to role-play as a grumpy attending who pushes back — helping the nurse practice under pressure." },
  { id:"critical",   icon:"⚡", color:B.pink,   title:"Critical Reasoning",     sub:"Student + Nurse", desc:"Not just what to do — but WHY, what to rule out, and what happens if you miss it.", ph:"Describe the clinical scenario you are thinking through...", sys:"Walk through every scenario: possible causes, most dangerous to rule out first, priority action and why, what happens if missed. Push the learner to think like a clinician." },
  { id:"labs",       icon:"🔬", color:B.cyan,   title:"Lab Interpreter",         sub:"Student + Nurse", desc:"Not just normal ranges — clinical meaning and exactly what to do RIGHT NOW.", ph:"Paste lab values (e.g. Na 128, K 6.2, Cr 4.1, BUN 88)...", sys:"Explain which values are abnormal, most likely clinical cause, dangerous implications, what to assess immediately, what to report to the physician, and what interventions to anticipate." },
  { id:"meds",       icon:"💊", color:B.green,  title:"Med & IV Safety Check",   sub:"Nurse Mode",     desc:"Check medication safety, IV compatibility, Y-site compatibility, drip rates, and high alert drug considerations — before you give it.", ph:"Enter medications and patient info...", sys:"Identify drug-drug interactions and severity, contraindications, safe dose ranges, what to monitor before/during/after, what to hold and call about. Be practical and bedside-focused.\n\nIV COMPATIBILITY SECTION:\nWhen nurse asks about IV compatibility also check and state:\n- Y-site compatibility (can these run simultaneously?)\n- Same line or separate line required?\n- Central line vs peripheral — which is required?\n- Compatible flush solutions (NS vs D5W vs LR)\n- Stability time once mixed\n- Light sensitivity — does it need to be covered?\n- Filter requirements\n- Rate limitations\n\nCOMMON IV INCOMPATIBILITIES TO ALWAYS FLAG:\n- Phenytoin: NS only — never D5W — never mixed with anything\n- Amphotericin B: D5W only — no electrolytes in same line\n- Ceftriaxone: never with calcium-containing solutions\n- Sodium bicarbonate: incompatible with most medications\n- Amiodarone: non-PVC tubing required — protect from light\n- Nitroglycerin: non-PVC tubing required — glass bottle preferred\n- Insulin: always separate line — never mix\n- TPN: dedicated line always — nothing else in that lumen\n- Blood products: NS only — never with anything else" },
  { id:"ekg",        icon:"📈", color:B.pink,   title:"EKG Interpreter",         sub:"Nurse Mode",     desc:"Snap or describe the strip — get plain-English interpretation with clinical action steps.", ph:"Describe the EKG strip (rate, rhythm, P waves, PR, QRS) — or attach a photo using 📎...", proOnly: true, sys: EKG_SYSTEM_PROMPT },
  { id:"prioritize", icon:"🎯", color:B.yellow, title:"Patient Prioritizer",     sub:"Nurse Mode",     desc:"Tell me your patients — I will rank who to see first and what to safely delegate.", ph:"Describe your patients (Room, diagnosis, current status)...", sys:"Rank patients by priority using ABCDE and clinical acuity. For each: reasoning, greatest risk, what to delegate. Think like a charge nurse. Flag anything needing immediate physician notification." },
  { id:"delegation", hidden:true, icon:"🤝", color:B.cyan,   title:"Delegation Coach",        sub:"Student + Nurse", desc:"What can you delegate? Practice the 5 rights with real scenarios.", ph:"Describe the task and who you are considering delegating to...", sys:"Apply the 5 Rights of Delegation to any scenario. Explain what can/cannot be delegated to CNAs and LPNs. Walk through clinical reasoning. Be clear about what the RN must retain responsibility for." },
  { id:"nclex", hidden:true, icon:"📚", color:B.green,  title:"NCLEX Coach",             sub:"Student Mode",   desc:"NGN-style questions with full clinical reasoning explained. Think like the test.", ph:"Ask for a practice question or paste one you are struggling with...", sys:"Generate NGN-style questions: bow-tie, matrix, extended multiple response. Explain complete clinical reasoning behind correct answers and why each distractor is wrong." },
  { id:"burnout",    icon:"💕", color:B.pink,   title:"Burnout Check-In",        sub:"Student + Nurse", desc:"Anonymous. No judgment. Talk to someone who actually gets what nursing does to a person.", ph:"Tell me how you are really doing. This is a safe space...", sys:"You are Big Sis. Respond with warmth and zero judgment. Validate feelings first. Ask gentle questions. Offer practical coping that actually works for nurses." },
  { id:"report",     icon:"🏥", color:B.cyan,   title:"Shift Report Generator",  sub:"Nurse Mode",     desc:"Dump your brain — get a structured, complete shift report ready in 2 minutes.", ph:"Tell me about your patient in any order — I will organize it...", sys:"Transform any disorganized patient information into a structured shift report: ID/diagnosis, significant events, current status by system, medications, pending orders, what to watch for, anticipated needs." },
  { id:"drip",       icon:"💧", color:B.yellow, title:"Drip Calculator",          sub:"Nurse Mode",     desc:"IV drip rates, weight-based dosing, titration math — step by step every time.", ph:"Enter your calculation (e.g. Dopamine 5 mcg/kg/min, 80kg, 400mg/250mL)...", sys:"Solve IV drip rate calculations step by step. Show every formula and conversion. Include final answer with units and a safety check." },
  { id:"interview",  icon:"💼", color:B.green,  title:"Interview Prep",           sub:"Student + Nurse", desc:"For nurses transferring to a new unit, escaping a toxic workplace, or ready for the next chapter in their nursing career.", ph:"Tell me the job, then ask for a question or paste your answer for feedback...", sys:"Generate realistic interview questions, coach STAR-method answers, give honest feedback. Flag what interviewers are really looking for. Be encouraging but honest." },
  { id:"scope", hidden:true, icon:"⚖️", color:B.pink,   title:"Scope of Practice",       sub:"Student + Nurse", desc:"Is this in your scope? Real situations, real answers, real language you can use right now.", ph:"Describe the situation you are unsure about...", sys:"State whether a situation falls within RN scope, obligations, how to handle boundary violations professionally, what to document, when to involve charge nurses or risk management." },
  { id:"incident", hidden:true, icon:"🚨", color:B.yellow, title:"Incident Report Helper",  sub:"Nurse Mode",     desc:"Med error, fall, near miss, equipment failure — get a clean, objective variance report fast.", ph:"Describe what happened (who, when, where, sequence of events)...", sys:"Generate a complete, professional incident/variance report. Use objective, factual language only — no blame, no opinion, no speculation. Structure it with: date/time/location, persons involved (use roles like 'RN' or 'patient' rather than names unless the user provided them), clear chronological sequence of events, immediate clinical actions taken, notifications made (provider, charge nurse, family), any witnesses, patient outcome and current status, and follow-up plan. Use third person, past tense. Avoid words like 'accidentally,' 'failed to,' 'should have' — stick to what was observed and done. Make it legally defensible and aligned with standard hospital risk-management language. End with a brief reminder that this is a draft to inform the user's official facility report and they should still follow their hospital's specific incident reporting policy and forms." },
  { id:"abbrev", hidden:true, icon:"🔤", color:B.green,  title:"Abbreviation Decoder",    sub:"Student + Nurse", desc:"Paste any chart shorthand, order, or note. Get plain-English meaning + a flag on dangerous abbreviations.", ph:"Paste an abbreviation or full line you can't decipher (e.g. 'pt c/o SOB, hx of CHF, gave 40mg furosemide IVP')...", sys:"You are an expert nursing reference for medical abbreviations. For any abbreviation or chart entry the user pastes:\n\n1. Decode every abbreviation. Give the full term and a brief plain-English meaning. If multiple meanings exist (e.g. 'PT' = physical therapy OR patient OR prothrombin time), list the most likely one for the context, then alternatives.\n\n2. CRITICAL — flag any abbreviation on the ISMP/Joint Commission 'Do Not Use' list. These cause real medication errors. Examples: QD, QOD, q.d., q.o.d. (use 'daily'/'every other day'), U or u (use 'units'), IU (use 'international units'), MS or MSO4 (use 'morphine sulfate'), MgSO4 (use 'magnesium sulfate'), cc (use 'mL'), μg (use 'mcg'), AS/AD/AU and OS/OD/OU (use 'right ear/left ear/each ear' or 'right eye/left eye/each eye'), nightly HS (use 'nightly'), trailing zeros like 1.0mg (use 1mg), naked decimals like .5mg (use 0.5mg). When you see one of these, mark it clearly with ⚠️ and state the safer alternative.\n\n3. If the user pastes an order or note, decode the WHOLE line — every abbreviation in it — then summarize what's clinically going on in 1-2 sentences.\n\n4. If unclear which meaning is intended, ask one quick clarifying question (specialty, context, what came right before/after).\n\n5. Be direct and brief. No fluff. The nurse is reading this on a 5-minute break and needs the answer fast.\n\nEnd with a one-line reminder: 'When in doubt, clarify with the prescriber — never assume.'" },
  { id:"rationale", hidden:true, icon:"💡", color:B.cyan,   title:"Rationale Explainer",      sub:"Student + Nurse", desc:"Get any nursing action, med, or lab value explained in plain language an 8th grader could understand.", ph:"Paste a nursing intervention, medication, lab value, or clinical action you want explained...", sys:"Explain any nursing intervention, medication, lab value, or clinical action in plain language an 8th-grader could understand. Use a 'why this happens' framework. Avoid textbook language. Use analogies when helpful. For each topic provide:\n\n1. What's actually happening physiologically (in simple words).\n2. Why nurses do or check this.\n3. What would happen if you didn't.\n4. How to remember it (mnemonic, analogy, or simple visualization).\n5. End with a 'simple way to put it' — one sentence summary you could say to a patient.\n\nNever lecture. Use 'imagine' and 'think of it like' often. The goal is for the student to walk away thinking 'oh THAT'S why we do that' — not memorizing more terms." },
  { id:"clinrot", hidden:true, icon:"🎓", color:B.pink,   title:"Clinical Rotation Survival", sub:"Student Mode",   desc:"How to impress your instructor, what NEVER to say, and surviving your first day on a new unit.", ph:"Tell me about your rotation or what you're nervous about...", sys:"You are a wise nurse mentor giving practical, real-world advice for clinical rotations. The student will describe their rotation, fear, or situation. Help them with:\n\n- What to say when you don't know (never 'I don't know' alone — always paired with 'but here's what I can find out').\n- How to impress your clinical instructor without being a kiss-up.\n- What you should NEVER do or say.\n- How to prepare the night before.\n- What to bring (specific items).\n- How to talk to patients for the first time without freezing.\n- Common mistakes new students make on each unit type.\n- How to handle a difficult instructor.\n- The one thing that will earn you respect on your first day.\n\nGive specific scripts they can use word-for-word. Be warm but practical, like Big Sis who's been there. Never lecture. Acknowledge the fear and then equip them." },
  { id:"patiented", hidden:true, icon:"🩺", color:B.green,  title:"Patient Education Builder", sub:"Student + Nurse", desc:"Plain-language patient teaching ready to print. Disease, meds, diet, warning signs, follow-up — covered.", ph:"Describe the patient's diagnosis and reading level (e.g. '62yo new-onset CHF, English isn't first language, 8th grade reading')...", sys:"Generate complete patient education materials in plain language at the literacy level the user specifies (default to 6th grade). Structure the output with these clear sections:\n\n1. **What you have, in simple words** — the diagnosis explained without medical jargon, with an analogy if helpful.\n2. **Your medications** — for each: what it does (in plain words), when to take it, common side effects to watch for.\n3. **Diet & lifestyle** — specific dos and don'ts, not vague advice.\n4. **Activity** — what's safe, what's not, when to start.\n5. **Warning signs — call your doctor IMMEDIATELY if** — bullet list of red flags.\n6. **Follow-up** — when, with whom, what to bring.\n7. **Questions to ask your provider** — give the patient 3-5 specific questions to ask.\n\nEnd with a line: 'Reviewed by RN: ___ Date: ___'. Format ready to print/copy. No medical jargon unless absolutely necessary, and define it when used. End with the educational disclaimer paragraph for the user (the nurse) reminding them this is a draft for their facility's review process." },
  { id:"drugcard", hidden:true, icon:"🃏", color:B.yellow, title:"Drug Card Generator",      sub:"Student + Nurse", desc:"On-demand complete drug card. Mechanism, doses, nursing implications, the one thing to never miss.", ph:"Enter any medication name (generic or brand)...", sys:"Generate a complete, exam-ready drug card for the medication the user enters. Use these exact sections:\n\n1. **Generic name** (and brand names)\n2. **Drug class & subclass**\n3. **Mechanism of action** — in 1-2 plain-language sentences (no jargon).\n4. **Therapeutic indications** — what it's actually used for.\n5. **Typical dose range** (note this is reference only; defer to prescriber's order).\n6. **Contraindications** — when NEVER to give it.\n7. **Adverse effects** — split into 'common' and 'serious/life-threatening'.\n8. **Nursing implications** — split into:\n   - Before administering (what to assess)\n   - During (what to monitor)\n   - After (what to reassess)\n9. **Patient teaching** — 3-5 specific points the patient must know.\n10. **Key drug interactions** — the most important ones.\n11. **⚠️ The ONE thing to never miss** — the single most dangerous error you could make with this drug.\n\nBe clinically accurate and bedside-focused. End with: 'Always verify against an authoritative drug reference (Lexicomp, Micromedex, or your facility pharmacy) before administration.'" },
  { id:"nandalib", hidden:true, icon:"📖", color:B.cyan,   title:"Nursing Diagnosis Library", sub:"Student Mode",   desc:"NANDA diagnoses explained in real-world language with clinical examples. No more textbook recitation.", ph:"Enter a NANDA nursing diagnosis OR describe a patient situation and I'll suggest the right diagnosis...", sys:"Help nursing students understand NANDA nursing diagnoses in clear, real-world language. The user will either enter a specific diagnosis (e.g., 'Ineffective Airway Clearance') OR describe a patient and ask which diagnosis fits.\n\nIf they enter a diagnosis, provide:\n1. **What it actually means** — in plain words, not textbook recitation.\n2. **Real clinical example** — describe a specific patient case where you'd use it.\n3. **When to use this diagnosis vs. similar ones** (and when NOT to use it).\n4. **Related factors** commonly seen with this diagnosis.\n5. **Defining characteristics** (objective + subjective signs).\n6. **Sample SMART goals** for this diagnosis.\n7. **Common nursing interventions** with brief rationale.\n\nIf they describe a patient, suggest 2-3 fitting diagnoses, ranked by priority, with brief reasoning for each.\n\nGoal: stop students from memorizing textbook language and help them actually understand what each diagnosis describes clinically. Use real patient scenarios over abstract definitions." },
  { id:"homework", hidden:true, icon:"✏️", color:B.yellow, title:"Assignment Helper",         sub:"Student Mode",   desc:"Paste your nursing assignment. Get coached through clinical reasoning — not handed an answer to copy.", ph:"Paste your nursing assignment question or topic...", sys:"You are a Socratic nursing tutor. The student will paste an assignment question. Your goal is to teach them clinical reasoning — NOT to give them an answer they can copy and paste.\n\nFor any assignment:\n\n1. First, identify what type of question it is (care plan, case study, concept analysis, reflection, drug study, etc.).\n2. Walk them through the thinking process step-by-step using Socratic questioning:\n   - What do you already know about this?\n   - What information do you need to find?\n   - What's your initial hypothesis?\n   - What evidence would support or refute it?\n   - What would a nurse actually do here, and why?\n3. Help them organize their thinking into the structure their assignment requires.\n4. Provide a framework or outline they can fill in — but in their own words.\n5. End with: 'Now write it in YOUR words. The thinking is yours; I just helped you organize it.'\n\nNEVER give them a copy-paste answer. If they push for one, gently redirect: 'I'm not going to write it for you — but I will help you think it through until you can write it yourself.' This is for their long-term clinical reasoning, not just one assignment grade.\n\nIf the assignment is a care plan or case study, point them to the Care Plan Builder tool for additional support." },
  { id:"ien_translator", icon:"🌍", color:B.cyan,   title:"IEN Translator",             sub:"International Nurse", desc:"Translate medical terms, abbreviations, and American nursing language to your home country's equivalent — and vice versa.", ph:"Enter an American nursing term, abbreviation, or phrase to translate...", badge:"IEN Mode", sys:"You are a bilingual clinical translator specializing in helping Internationally Educated Nurses navigate the American healthcare system. You bridge the gap between nursing education from other countries and American clinical practice.\n\nYour job: Translate American medical abbreviations, nursing jargon, documentation styles, medication names, scope of practice differences, hospital hierarchy, and clinical terminology for internationally educated nurses. Always be warm, patient, and culturally sensitive. IENs have tremendous skill — they just need the translation layer. Never make them feel inferior.\n\nCover: Abbreviations (PRN, QID, TID, BID, NPO, HOB, SOB, CP), charting styles (MAR, SBAR), American vs international drug names (paracetamol vs acetaminophen, adrenaline vs epinephrine), vital sign normal ranges, American grading scales (GCS, pain 0-10, Braden, Morse fall scale), American workplace culture (chain of command, charge nurse, rapid response team, code blue).\n\nFormat responses: Term explained, American usage in context, international equivalent if applicable, example in a clinical sentence." },
  { id:"ien_nclex", hidden:true, icon:"📋", color:B.pink,   title:"NCLEX for IENs",             sub:"International Nurse", desc:"NCLEX coaching built specifically for internationally educated nurses — bridging the gap between your training and the American nursing boards.", ph:"Ask me anything about NCLEX or paste a question you are struggling with...", badge:"IEN Mode", sys:"You are an NCLEX coach specializing in helping Internationally Educated Nurses pass the NCLEX-RN or NCLEX-PN. You understand that IENs often have excellent clinical knowledge but struggle with American testing style, cultural context, and NCLEX-specific reasoning.\n\nKey areas: NCLEX reasoning (client safety FIRST always), NCLEX language (client not patient), Maslow's hierarchy in NCLEX, ABC priority framework, American medication names, American scope of practice, cultural considerations (informed consent, patient autonomy, HIPAA), NCLEX Next Generation question types, test-taking strategies. Always validate the IEN's existing knowledge. Explain the WHY behind NCLEX answers. Build confidence — IEN pass rates improve dramatically with the right preparation." },
  { id:"ien_charting",   icon:"📝", color:B.green,  title:"American Charting Adapter",  sub:"International Nurse", desc:"Transform your nursing notes into American documentation style — legally sound, professionally worded, ready for the American chart.", ph:"Paste your nursing note and I will adapt it to American charting style...", badge:"IEN Mode", sys:"You are an expert in American nursing documentation standards. You help Internationally Educated Nurses transform their nursing notes into American-style charting that meets US legal, regulatory, and professional standards.\n\nAmerican documentation standards: SOAP notes, SBAR, DAR notes, narrative nursing notes, MAR documentation, incident reports, shift handoff report style. American charting rules: always past tense, objective language only, document time and date, if it was not charted it was not done legally, never chart in advance, always document patient response to interventions, document physician notification with name time and response.\n\nWhen an IEN gives their note — rewrite it in American style while preserving all clinical content. Explain what was changed and why.\n\nFormat: Original note provided — American-adapted version — What was changed and why — Tips for next time." },
  { id:"concept_map", hidden:true, icon:"🗺️", color:B.yellow, title:"Concept Map Generator",      sub:"Student + Nurse", desc:"Enter a disease or patient scenario — get an interactive visual concept map with pathophysiology, nursing diagnoses, interventions, and connections.", ph:"Enter a disease, condition, or patient scenario (e.g. 'CHF with BNP 1200' or 'DKA')...", badge:"Student + Nurse", sys:"You are a nursing education expert. Return ONLY a valid JSON object — no markdown, no extra text, just the JSON. Structure:\n{\"title\":\"Condition Name\",\"nodes\":[{\"id\":\"1\",\"type\":\"main\",\"label\":\"Short label\",\"details\":\"Full clinical description\"},{\"id\":\"2\",\"type\":\"patho\",\"label\":\"Patho step\",\"details\":\"Explanation\"}],\"edges\":[{\"id\":\"e1-2\",\"source\":\"1\",\"target\":\"2\",\"type\":\"patho\"}]}\n\nNode types: main (1 node — center diagnosis), patho (2-3 pathophysiology steps), nursing_dx (2-3 NANDA diagnoses), intervention (3-4 nursing interventions), complication (2-3 complications), medication (2-3 key medications), education (2-3 patient education points). Labels max 4 words. Put full explanations in details. Connect nodes with clinically logical edges showing cause/effect relationships." },
  { id:"adaptive_study", hidden:true, icon:"🎯", color:B.pink,   title:"Adaptive Study Mode",        sub:"Student + Nurse", desc:"Daily clinical questions that adapt to what you get wrong. The more you practice — the smarter your questions get.", ph:"Ask for a question, specify a topic, or tell me what you got wrong last time...", badge:"Student + Nurse", sys:"You are an adaptive nursing education system. Generate targeted NCLEX-style questions that help nurses identify and improve weak areas. Generate ONE question at a time using NCLEX Next Generation format — scenario-based not just recall. After user answers provide detailed rationale for correct AND incorrect options.\n\nTopics: Cardiac, Respiratory, Neurological, Renal, Endocrine, Pharmacology, OB, Pediatrics, Mental Health, Priority and Delegation, Infection Control, Safety, Fluid and Electrolytes, Perioperative Care.\n\nQuestion format:\nCLINICAL SCENARIO: [2-3 sentence patient scenario]\nQUESTION: [Clear specific question]\nOPTIONS: A. B. C. D.\n\nAfter answer:\nCORRECT ANSWER: [letter and explanation]\nRATIONALE FOR CORRECT ANSWER: [detailed clinical reasoning]\nWHY THE OTHERS ARE WRONG: A, B, C, D explanations\nNCLEX TIP: [how to approach this type of question]" },
  { id:"speak_up",         icon:"🛡️", color:B.pink,   title:"Speak Up Tool",              sub:"Nurse Mode",     desc:"Word-for-word scripts for bullying, intimidation, and disrespect on the floor. You deserve to be treated with dignity.", ph:"Tell me what happened — use exact words if you remember them...", sys: SPEAK_UP_SYSTEM_PROMPT },
  { id:"document_protect", icon:"🔒", color:B.cyan,   title:"Document & Protect",         sub:"Nurse Mode",     desc:"Timestamped legal documentation for heavy assignments, bullying, unsafe staffing, and anything you need on record. Protect your license.", ph:"Describe what happened — date, time, location, who was involved, and exact words if you remember them...", sys: DOCUMENT_PROTECT_SYSTEM_PROMPT },
  { id:"rapid_response",   icon:"🚨", color:B.red,    title:"Rapid Response Coach",       sub:"Nurse Mode",     desc:"Step-by-step guidance when your patient is deteriorating. Stay calm. Stay focused. You've got this.", ph:"Describe your patient's vitals and what's changing — I'll walk you through what to do right now...", sys: RAPID_RESPONSE_SYSTEM_PROMPT },
  { id:"family_comm",      icon:"👨‍👩‍👧", color:B.green,  title:"Family Communication Coach", sub:"Nurse Mode",     desc:"Scripts for difficult families, delivering bad news, de-escalation, and navigating tough conversations with confidence.", ph:"Tell me what's happening with the family — and what you want help saying...", sys: FAMILY_COMM_SYSTEM_PROMPT },
  { id:"nurse_journal",    icon:"📔", color:B.purple, title:"Nurse Journal",              sub:"Student + Nurse", desc:"Your private space to process, vent, reflect, and grow. What happens here stays here.", ph:"Write anything — vent, reflect, celebrate. This stays private.", sys: NURSE_JOURNAL_SYSTEM_PROMPT },
  { id:"head_to_toe",      icon:"🩺", color:B.cyan,   title:"Head-to-Toe Assessment Coach", sub:"Student + Nurse", desc:"Systematic guidance through every body system. Normal vs abnormal explained. Assessment note generated automatically.", ph:"Tell me about your patient — I'll walk you through each system and build the assessment note...", sys: HEAD_TO_TOE_SYSTEM_PROMPT },
  { id:"pharm_coach",      icon:"💊", color:B.yellow, title:"Pharmacology Coach",         sub:"Student + Nurse", desc:"New to a medication? Get plain language explanation, nursing considerations, and what to monitor — before you give it.", ph:"Enter the medication you're about to give — I'll walk you through what you need to know...", sys: PHARM_COACH_SYSTEM_PROMPT },
  { id:"my_schedule",      icon:"📅", color:B.cyan,   title:"My Schedule",                sub:"Nurse Mode",     desc:"Track your shifts, document shift swaps, monitor overtime, and build a recheck timeline for labs and meds during your shift.", ph:"Tell me about your shift — schedule, swap, overtime, or set a clinical reminder...", sys: MY_SCHEDULE_SYSTEM_PROMPT },
  { id:"shift_planner",    icon:"⏰", color:B.green,  title:"Shift Planner",              sub:"Nurse Mode",     desc:"Tell me your patients — I'll build your entire shift plan with priorities, time blocks, and what to do first.", ph:"Give me your patient assignment — rooms, diagnoses, anything pending — and I'll build your shift roadmap...", sys: SHIFT_PLANNER_SYSTEM_PROMPT },
  { id:"pt_education",     icon:"🖨️", color:B.pink,   title:"Patient & Family Education", sub:"Nurse Mode",     desc:"Generate plain language patient education handouts for any diagnosis, procedure, or medication — printable and family friendly.", ph:"Tell me the diagnosis, procedure, or medication you need a handout for — and the reading level or Spanish if needed...", sys: PT_EDUCATION_SYSTEM_PROMPT },
];

const FREE_SESSION_LIMIT = 5;

const getUsage = () => {
  const d = JSON.parse(localStorage.getItem("rn101_usage") || "{}");
  // Lifetime count — no monthly reset. Legacy "month" field (if present) is ignored;
  // existing per-month counts carry over as the user's new lifetime count.
  return { count: d.count || 0 };
};
const addUsage = () => {
  const d = getUsage();
  d.count = (d.count || 0) + 1;
  localStorage.setItem("rn101_usage", JSON.stringify(d));
  return d.count;
};

// PWA install helpers — used by the "Install on your phone" flow.
function detectInstallPlatform() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  if (isIOS) return "ios";
  if (isAndroid) return "android";
  return "unknown";
}
function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  if (window.navigator && window.navigator.standalone === true) return true; // iOS Safari
  return false;
}

const getUser  = () => JSON.parse(localStorage.getItem("rn101_user") || "null");
const saveUser = u  => localStorage.setItem("rn101_user", JSON.stringify(u));
const nukeUser = () => localStorage.removeItem("rn101_user");
const getDisclaimerAccepted = () => {
  try {
    const stored = localStorage.getItem("rn101_disclaimer_v2");
    if (!stored) return false;
    const { accepted, timestamp } = JSON.parse(stored);
    if (!accepted) return false;
    return (Date.now() - timestamp) / (1000 * 60 * 60 * 24) < 90;
  } catch(e) { return false; }
};
const setDisclaimerAccepted = () => {
  localStorage.setItem("rn101_disclaimer_v2", JSON.stringify({ accepted: true, timestamp: Date.now() }));
};

function openPrint(toolTitle, question, response) {
  const w = window.open("", "", "width=800,height=600");
  if (!w) { alert("Please allow popups to print."); return; }
  const esc = s => String(s == null ? "" : s).replace(/[&<>"']/g, c => ({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;" }[c]));
  // marked.parse escapes user content automatically; markdown syntax becomes the proper HTML tags.
  const responseHtml = marked.parse(response || "");
  const css = `body{font-family:'Segoe UI',Arial,sans-serif;padding:48px;max-width:680px;margin:0 auto;line-height:1.5;color:#222;font-size:14pt}.letterhead{text-align:center;padding-bottom:18px;border-bottom:2px solid #222;margin-bottom:24px}.letterhead .brand{font-size:24pt;font-weight:800;letter-spacing:-.5px;color:#12172B}.letterhead .tag{font-size:10pt;color:#888;margin-top:4px;letter-spacing:2px;text-transform:uppercase}h1{font-size:18pt;font-weight:700;margin:0 0 18px}.q{font-style:italic;color:#555;margin:18px 0;padding-left:14px;border-left:3px solid #ccc;font-size:13pt}.r{font-size:14pt}.r h1{font-size:16pt;font-weight:800;margin:16px 0 8px}.r h2{font-size:14pt;font-weight:700;margin:14px 0 6px}.r h3{font-size:13pt;font-weight:700;margin:12px 0 4px}.r h4{font-size:12pt;font-weight:700;margin:10px 0 4px}.r p{margin:0 0 10px}.r ul,.r ol{margin:8px 0 12px;padding-left:24px}.r li{margin:4px 0}.r blockquote{border-left:3px solid #888;padding-left:12px;margin:10px 0;color:#555;font-style:italic}.r hr{border:none;border-top:1px solid #ccc;margin:16px 0}.r strong{font-weight:800}.r em{font-style:italic}.r code{background:#f0f0f0;padding:2px 5px;border-radius:3px;font-family:'Courier New',monospace;font-size:13pt}.r pre{background:#f0f0f0;padding:10px;border-radius:5px;overflow:auto;font-size:12pt}.r table{border-collapse:collapse;width:100%;margin:10px 0;font-size:13pt}.r th{border:1px solid #888;padding:6px 10px;background:#eee;font-weight:700;text-align:left}.r td{border:1px solid #ccc;padding:6px 10px}.r a{color:#0066cc;text-decoration:underline}.sig{margin-top:60px;padding-top:14px;border-top:1px solid #888}.sig-line{display:flex;align-items:flex-end;gap:8px;margin-top:28px;font-size:11pt;color:#555}.sig-line .line{flex:1;border-bottom:1px solid #555;height:22px}.sig-line .line.short{max-width:180px}.f{margin-top:30px;padding-top:14px;border-top:1px solid #eee;font-size:9pt;color:#888;text-align:center}@media print{body{padding:24px}}`;
  w.document.write(`<!DOCTYPE html><html><head><title>${esc(toolTitle)} — ShiftMateRN</title><style>${css}</style></head><body><div class="letterhead"><div class="brand">ShiftMateRN</div><div class="tag">Nursing AI Coach</div></div><h1>${esc(toolTitle)}</h1>${question?`<div class="q">${esc(question)}</div>`:""}<div class="r">${responseHtml}</div><div class="sig"><div class="sig-line">Nurse signature:<div class="line"></div></div><div class="sig-line">Date:<div class="line short"></div></div></div><div class="f">Generated by ShiftMateRN — Educational tool, not a substitute for hospital protocols. ${esc(new Date().toLocaleString())}</div></body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => { try { w.print(); } catch(e){} }, 350);
}

function buildContentForUserTurn(text, image) {
  if (image && image.base64 && image.mediaType) {
    return [
      { type: "image", source: { type: "base64", media_type: image.mediaType, data: image.base64 } },
      { type: "text", text: text || "What can you tell me about this image?" },
    ];
  }
  return text || "";
}

async function callAI(system, history, msg, image, toolId) {
  const messages = [];
  for (const h of (history || [])) {
    if (h.role === "user") {
      messages.push({ role: "user", content: buildContentForUserTurn(h.content, h.image) });
    } else if (h.role === "assistant") {
      messages.push({ role: "assistant", content: h.content || "" });
    }
  }
  messages.push({ role: "user", content: buildContentForUserTurn(msg, image) });

  const hasAnyImage = !!image || (history || []).some(h => h.image && h.image.base64);
  let model, max_tokens;
  if (toolId === "ekg") {
    model = "claude-sonnet-4-6";
    max_tokens = 4000;
  } else {
    model = hasAnyImage ? "claude-opus-4-6" : "claude-sonnet-4-6";
    max_tokens = hasAnyImage ? 2000 : 1500;
  }

  const r = await fetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ model, max_tokens, system, messages }),
  });
  if (!r.ok) throw new Error(r.status);
  return (await r.json()).content[0].text;
}

// ─── VOICE INPUT (Speech Recognition) ───────────────────────
function getSpeechRecognition() {
  return window.SpeechRecognition || window.webkitSpeechRecognition;
}

function startVoiceRecognition(onTranscript, onEnd, onError) {
  const Recognition = getSpeechRecognition();
  if (!Recognition) return null;
  const r = new Recognition();
  r.lang = "en-US";
  r.continuous = true;
  r.interimResults = true;
  r.onresult = (event) => {
    let final = "";
    for (let i = 0; i < event.results.length; i++) {
      final += event.results[i][0].transcript;
    }
    onTranscript(final);
  };
  r.onend = onEnd;
  r.onerror = (e) => { onError && onError(e); onEnd && onEnd(); };
  try { r.start(); } catch(e){ return null; }
  return r;
}

// ─── VOICE OUTPUT (Speech Synthesis) ─────────────────────────
// ─── TTS TEXT CLEANER ────────────────────────────────────────
function cleanForTTS(text) {
  return (text || "")
    .replace(/\n+/g, '. ')
    .replace(/—|–|−|-/g, ' ')
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .replace(/`/g, '')
    .replace(/#+\s/g, '')
    .replace(/[•●▪◦∙·]/g, ',')
    .replace(/→|←|↑|↓/g, ',')
    .replace(/[<>]/g, '')
    .replace(/\[|\]|\(|\)/g, '')
    .replace(/_{2,}/g, '')
    // Strip emojis — TTS otherwise reads them as "police car light" etc.
    .replace(/\p{Extended_Pictographic}/gu, '')
    // Strip the invisible joiners left behind by compound emojis (e.g. 👨‍👩‍👧):
    // ZWJ, Variation Selector-16, Combining Enclosing Keycap.
    .replace(/[‍️⃣]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Warm up the voice list at module load. Chrome/Edge populate lazily —
// without this, the first getVoices() call returns [] and our priority
// chain falls through to the system default (often male).
if (typeof window !== "undefined" && "speechSynthesis" in window) {
  window.speechSynthesis.getVoices();
}

const MALE_VOICE_RE = /\b(male|david|mark|richard|george|alex|daniel|fred|bruce|reed|rocko|grandpa|albert|junior|ralph)\b/i;

function pickFemaleVoice() {
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  const en = voices.filter(v => /^en[-_]/i.test(v.lang) || v.lang === "en");
  const pool = en.length ? en : voices;
  return (
    pool.find(v => /google us english female/i.test(v.name)) ||
    pool.find(v => /google uk english female/i.test(v.name)) ||
    pool.find(v => /microsoft (zira|aria|hazel|susan|jenny|michelle)/i.test(v.name)) ||
    pool.find(v => /samantha/i.test(v.name)) ||
    pool.find(v => /karen|moira|tessa|fiona|veena|catherine/i.test(v.name)) ||
    pool.find(v => /google/i.test(v.name) && /female/i.test(v.name)) ||
    pool.find(v => /female/i.test(v.name)) ||
    pool.find(v => !MALE_VOICE_RE.test(v.name)) ||
    pool[0]
  );
}

function speakResponse(text, onEnd) {
  if (!("speechSynthesis" in window)) { onEnd && onEnd(); return null; }
  window.speechSynthesis.cancel();
  const clean = cleanForTTS(text);

  const doSpeak = () => {
    const u = new SpeechSynthesisUtterance(clean);
    u.rate = 0.90;
    u.pitch = 1.05;
    u.volume = 1.0;
    u.lang = "en-US";
    const preferred = pickFemaleVoice();
    if (preferred) u.voice = preferred;
    u.onend = () => onEnd && onEnd();
    u.onerror = () => onEnd && onEnd();
    window.speechSynthesis.speak(u);
  };

  // Fast path: voices already loaded.
  if (window.speechSynthesis.getVoices().length) {
    doSpeak();
    return null;
  }

  // Slow path: wait for voiceschanged, with a setTimeout fallback for
  // Firefox which sometimes never fires the event.
  let triggered = false;
  const onReady = () => {
    if (triggered) return;
    triggered = true;
    window.speechSynthesis.removeEventListener("voiceschanged", onReady);
    doSpeak();
  };
  window.speechSynthesis.addEventListener("voiceschanged", onReady);
  setTimeout(onReady, 250);
  return null;
}

function stopSpeaking() {
  if ("speechSynthesis" in window) window.speechSynthesis.cancel();
}

async function fileToCompressedImage(file, maxSize = 1600) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          if (width > height) { height = Math.round(height * (maxSize / width)); width = maxSize; }
          else { width = Math.round(width * (maxSize / height)); height = maxSize; }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        const base64 = dataUrl.split(",")[1];
        resolve({ base64, mediaType: "image/jpeg", preview: dataUrl });
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const IS = { width: "100%", background: "rgba(255,255,255,.06)", border: "1.5px solid rgba(255,255,255,.12)", borderRadius: 12, padding: "13px 16px", color: "#fff", fontSize: 14, fontFamily: "'Poppins',sans-serif", outline: "none", marginBottom: 12, display: "block" };
const ABS = { background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8, padding: "5px 12px", color: "rgba(255,255,255,.5)", cursor: "pointer", fontSize: 11, fontFamily: "'Poppins',sans-serif", fontWeight: 600 };

function PHIBanner() {
  return (
    <div style={{ background: "rgba(255,45,120,.08)", border: "1px solid rgba(255,45,120,.25)", borderRadius: 8, padding: "6px 12px", marginBottom: 8, fontSize: 11, color: "rgba(255,255,255,.6)", display: "flex", alignItems: "center", gap: 6 }}>
      🔒 Do not enter real patient information — use general descriptions only (e.g. "68yo male with chest pain" — not patient names or MRNs)
    </div>
  );
}

const MD_STYLES = {
  h1: { fontSize: 18, fontWeight: 800, color: "#fff", margin: "16px 0 8px" },
  h2: { fontSize: 16, fontWeight: 800, color: "#fff", margin: "14px 0 6px" },
  h3: { fontSize: 14, fontWeight: 700, color: "#fff", margin: "12px 0 4px" },
  h4: { fontSize: 13, fontWeight: 700, color: "#fff", margin: "10px 0 4px" },
  p: { margin: "0 0 10px" },
  strong: { color: "#fff", fontWeight: 800 },
  em: { color: "rgba(255,255,255,.85)" },
  ul: { margin: "8px 0 12px", paddingLeft: 22 },
  ol: { margin: "8px 0 12px", paddingLeft: 22 },
  li: { margin: "4px 0", lineHeight: 1.7 },
  blockquote: { borderLeft: `3px solid ${B.cyan}55`, paddingLeft: 12, margin: "10px 0", color: "rgba(255,255,255,.7)", fontStyle: "italic" },
  hr: { border: "none", borderTop: "1px solid rgba(255,255,255,.18)", margin: "16px 0" },
  tableWrap: { overflowX: "auto", margin: "10px 0" },
  table: { borderCollapse: "collapse", width: "100%", fontSize: 13 },
  thead: { background: "rgba(255,255,255,.06)" },
  th: { padding: "8px 10px", textAlign: "left", borderBottom: `1.5px solid ${B.cyan}55`, color: "#fff", fontWeight: 700 },
  td: { padding: "8px 10px", borderBottom: "1px solid rgba(255,255,255,.1)", color: "rgba(255,255,255,.85)" },
  codeInline: { background: "rgba(255,255,255,.08)", padding: "2px 5px", borderRadius: 4, fontSize: 12, fontFamily: "Menlo, monospace" },
  pre: { background: "rgba(0,0,0,.3)", padding: 12, borderRadius: 8, overflow: "auto", margin: "8px 0", fontSize: 12, fontFamily: "Menlo, monospace" },
  a: { color: B.cyan, textDecoration: "underline" },
};

const MD_COMPONENTS = {
  h1: ({ node, children, ...p }) => <h1 style={MD_STYLES.h1} {...p}>{children}</h1>,
  h2: ({ node, children, ...p }) => <h2 style={MD_STYLES.h2} {...p}>{children}</h2>,
  h3: ({ node, children, ...p }) => <h3 style={MD_STYLES.h3} {...p}>{children}</h3>,
  h4: ({ node, children, ...p }) => <h4 style={MD_STYLES.h4} {...p}>{children}</h4>,
  p: ({ node, ...p }) => <p style={MD_STYLES.p} {...p} />,
  strong: ({ node, ...p }) => <strong style={MD_STYLES.strong} {...p} />,
  em: ({ node, ...p }) => <em style={MD_STYLES.em} {...p} />,
  ul: ({ node, ordered, ...p }) => <ul style={MD_STYLES.ul} {...p} />,
  ol: ({ node, ordered, ...p }) => <ol style={MD_STYLES.ol} {...p} />,
  li: ({ node, ordered, ...p }) => <li style={MD_STYLES.li} {...p} />,
  blockquote: ({ node, ...p }) => <blockquote style={MD_STYLES.blockquote} {...p} />,
  hr: () => <hr style={MD_STYLES.hr} />,
  table: ({ node, ...p }) => <div style={MD_STYLES.tableWrap}><table style={MD_STYLES.table} {...p} /></div>,
  thead: ({ node, ...p }) => <thead style={MD_STYLES.thead} {...p} />,
  th: ({ node, ...p }) => <th style={MD_STYLES.th} {...p} />,
  td: ({ node, ...p }) => <td style={MD_STYLES.td} {...p} />,
  code: ({ node, className, ...p }) => {
    const isBlock = /language-/.test(className || "");
    return isBlock ? <code className={className} {...p} /> : <code style={MD_STYLES.codeInline} {...p} />;
  },
  pre: ({ node, ...p }) => <pre style={MD_STYLES.pre} {...p} />,
  a: ({ node, children, ...p }) => <a style={MD_STYLES.a} target="_blank" rel="noreferrer" {...p}>{children}</a>,
};

function MarkdownText({ children }) {
  return <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{children || ""}</ReactMarkdown>;
}

function SamplePrompts({ toolId, onSelect }) {
  const prompts = SAMPLE_PROMPTS[toolId] || [];
  if (!prompts.length) return null;
  return (
    <div style={{ padding: "4px 18px 8px", display: "flex", gap: 6, flexWrap: "wrap" }}>
      {prompts.map((p, i) => (
        <button key={i} onClick={() => onSelect(p)} style={{ background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.15)", borderRadius: 20, color: "rgba(255,255,255,.65)", fontSize: 11, padding: "4px 10px", cursor: "pointer", fontFamily: "'Poppins',sans-serif", transition: "all .15s" }}>
          {p.length > 55 ? p.slice(0, 55) + "…" : p}
        </button>
      ))}
    </div>
  );
}

function FeedbackButtons({ msgIdx, toolId, msgContent, userId }) {
  const [rating, setRating] = useState(null);
  const [toast, setToast] = useState(false);

  async function handleFeedback(r) {
    if (rating) return;
    setRating(r);
    setToast(true);
    setTimeout(() => setToast(false), 2000);
    try {
      await supabase.from("tool_feedback").insert({
        user_id: userId || null,
        tool_id: toolId,
        message_content: (msgContent || "").slice(0, 500),
        rating: r,
      });
    } catch(e) { console.log("Feedback insert failed:", e); }
  }

  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end", marginTop: 4 }}>
      {toast && <span style={{ fontSize: 10, color: "rgba(255,255,255,.4)", fontStyle: "italic" }}>Thanks for your feedback!</span>}
      <button onClick={() => handleFeedback("positive")} style={{ background: "none", border: "none", cursor: rating ? "default" : "pointer", fontSize: 15, color: rating === "positive" ? B.green : "rgba(255,255,255,.25)", padding: "2px 4px", transition: "color .2s" }}>👍</button>
      <button onClick={() => handleFeedback("negative")} style={{ background: "none", border: "none", cursor: rating ? "default" : "pointer", fontSize: 15, color: rating === "negative" ? B.pink : "rgba(255,255,255,.25)", padding: "2px 4px", transition: "color .2s" }}>👎</button>
    </div>
  );
}

function Btn({ children, onClick, color = B.pink, outline, disabled, style = {} }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ background: outline ? "transparent" : disabled ? "rgba(255,255,255,.1)" : color, color: outline ? color : color === B.yellow ? B.navy : "#fff", border: outline ? `1.5px solid ${color}` : "none", borderRadius: 12, padding: "12px 22px", fontFamily: "'Poppins',sans-serif", fontWeight: 700, fontSize: 14, cursor: disabled ? "default" : "pointer", transition: "all .2s", opacity: disabled ? .5 : 1, ...style }}>
      {children}
    </button>
  );
}

function OnboardingFlow({ onComplete, onSkip, onShowInstall }) {
  const [step, setStep] = React.useState(0);
  const STEPS = [
    { icon: "🩺", title: "Welcome to ShiftMateRN 🩺", body: "Built by a cardiac nurse with 19 years experience. 24 tools built for the exact moments nurses need help most.", btn: "Next →" },
    { icon: "🎉", title: "Your first 5 sessions are free", body: "No credit card needed. Try any tool right now. Upgrade to Pro for unlimited access at any time.", btn: "Next →" },
    { icon: "🔒", title: "One rule before you start", body: "Never enter real patient information. Use general descriptions only — '68yo male with chest pain' not patient names or MRNs. This protects your patients and you.", btn: "Next →" },
    { icon: "📲", title: "Install ShiftMateRN on your phone", body: "Add it to your home screen so it opens like a real app — full screen, no browser bar, one tap from any clinical question.", btn: "Show me how →", installAction: true },
  ];
  const s = STEPS[step];
  function next() {
    if (s.installAction) { onShowInstall && onShowInstall(); onComplete(); return; }
    step < STEPS.length - 1 ? setStep(step + 1) : onComplete();
  }
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(18,23,43,.97)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'Poppins',sans-serif" }}>
      <button onClick={onSkip} style={{ position: "absolute", top: 20, right: 20, background: "transparent", border: "none", color: "rgba(255,255,255,.35)", cursor: "pointer", fontSize: 13, fontFamily: "'Poppins',sans-serif", fontWeight: 600 }}>Skip</button>
      <div style={{ maxWidth: 460, width: "100%", textAlign: "center" }}>
        <div style={{ fontSize: 52, marginBottom: 20 }}>{s.icon}</div>
        <h2 style={{ color: "#fff", fontWeight: 900, fontSize: 26, marginBottom: 16, letterSpacing: -0.5 }}>{s.title}</h2>
        <p style={{ color: "rgba(255,255,255,.72)", fontSize: 15, lineHeight: 1.7, marginBottom: 36, maxWidth: 380, margin: "0 auto 36px" }}>{s.body}</p>
        <Btn onClick={next} color={B.pink} style={{ padding: "14px 40px", fontSize: 15 }}>{s.btn}</Btn>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 28 }}>
          {STEPS.map((_, i) => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: i === step ? B.pink : "rgba(255,255,255,.2)", transition: "background .2s" }} />)}
        </div>
      </div>
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.78)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: B.navy2, borderRadius: 20, padding: 32, maxWidth: 480, width: "100%", border: "1px solid rgba(255,255,255,.1)", maxHeight: "90vh", overflowY: "auto" }}>
        {children}
      </div>
    </div>
  );
}

function InstructionBlock({ title, icon, steps }) {
  return (
    <div style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.08)", borderRadius: 12, padding: 16, marginBottom: 12 }}>
      <div style={{ fontWeight: 800, color: "#fff", fontSize: 14, marginBottom: 12 }}>{icon} {title}</div>
      <ol style={{ margin: 0, paddingLeft: 20, color: "rgba(255,255,255,.85)", fontSize: 13, lineHeight: 1.7 }}>
        {steps.map((s, i) => <li key={i} style={{ marginBottom: 6 }}>{s}</li>)}
      </ol>
    </div>
  );
}

function InstallInstructionsModal({ onClose, installPrompt, onTriggerInstall }) {
  const platform = detectInstallPlatform();
  const iosSteps = [
    "Tap the Share button (the square with an arrow pointing up) at the bottom of Safari.",
    "Scroll down and tap 'Add to Home Screen'.",
    "Tap 'Add' in the top-right corner.",
  ];
  const androidSteps = [
    "Tap the ⋮ menu in the top-right of Chrome.",
    "Tap 'Install app' or 'Add to Home screen'.",
    "Tap 'Install' to confirm.",
  ];
  return (
    <Modal onClose={onClose}>
      <div style={{ fontSize: 40, marginBottom: 14, textAlign: "center" }}>📲</div>
      <h2 style={{ color: "#fff", fontWeight: 900, fontSize: 22, marginBottom: 8, textAlign: "center", letterSpacing: -0.3 }}>Install ShiftMateRN on your phone</h2>
      <p style={{ color: "rgba(255,255,255,.65)", fontSize: 14, lineHeight: 1.65, textAlign: "center", marginBottom: 22 }}>
        Add ShiftMateRN to your home screen so it opens like a real app — full screen, no browser bar, one tap from any clinical question.
      </p>
      {platform === "android" && installPrompt && (
        <div style={{ marginBottom: 18 }}>
          <Btn onClick={onTriggerInstall} color={B.green} style={{ padding: "12px 20px", fontSize: 14, width: "100%" }}>⚡ Install now (one tap)</Btn>
          <div style={{ textAlign: "center", color: "rgba(255,255,255,.35)", fontSize: 11, marginTop: 8 }}>or follow the steps below</div>
        </div>
      )}
      {(platform === "ios" || platform === "unknown") && (
        <InstructionBlock title="iPhone or iPad (Safari)" icon="🍎" steps={iosSteps} />
      )}
      {(platform === "android" || platform === "unknown") && (
        <InstructionBlock title="Android (Chrome)" icon="🤖" steps={androidSteps} />
      )}
      <Btn onClick={onClose} outline color={B.cyan} style={{ padding: "10px 20px", fontSize: 13, width: "100%", marginTop: 12 }}>Got it</Btn>
    </Modal>
  );
}

const socialProofNotifications = [
  "Sarah M. from Texas just joined ShiftMateRN",
  "Ashley R. from Florida just upgraded to Pro",
  "Danielle K. from California just joined",
  "Monique T. from New York just upgraded to Founding Member",
  "Jessica L. from Georgia just joined ShiftMateRN",
  "Brittany H. from Ohio just upgraded to Pro",
  "Keisha W. from Illinois just joined",
  "Amanda P. from Pennsylvania just upgraded",
  "Tiffany M. from North Carolina just joined",
  "Lauren S. from Michigan just upgraded to Founding Member",
  "Jasmine C. from Virginia just joined ShiftMateRN",
  "Melissa R. from Arizona just upgraded to Pro",
  "Nicole B. from Tennessee just joined",
  "Stephanie J. from Washington just upgraded",
  "Courtney D. from Colorado just joined ShiftMateRN",
];

function SocialProofTicker() {
  const [idx, setIdx] = React.useState(0);
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const initial = setTimeout(() => setVisible(true), 1500);
    const cycle = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx(i => (i + 1) % socialProofNotifications.length);
        setVisible(true);
      }, 500);
    }, 60000);
    return () => { clearTimeout(initial); clearInterval(cycle); };
  }, []);
  return (
    <div style={{
      position: "fixed", bottom: 20, left: 20, zIndex: 50,
      background: B.navy2, border: `1px solid ${B.cyan}44`, borderRadius: 14,
      padding: "10px 14px", maxWidth: 320,
      boxShadow: "0 8px 24px rgba(0,0,0,.4)",
      display: "flex", alignItems: "center", gap: 10,
      fontSize: 12, color: "rgba(255,255,255,.85)",
      fontFamily: "'Poppins',sans-serif",
      transform: visible ? "translateX(0)" : "translateX(-110%)",
      opacity: visible ? 1 : 0,
      transition: "transform .4s ease, opacity .4s ease",
      pointerEvents: "none",
    }}>
      <div style={{ fontSize: 20 }}>👩‍⚕️</div>
      <div>{socialProofNotifications[idx]}</div>
    </div>
  );
}

function AuthModal({ mode: m0, onClose, onSuccess, onShowLegal }) {
  const [mode, setMode] = useState(m0 || "login");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  async function go() {
    setErr(""); setInfo("");
    if (!email || !pw) { setErr("Please fill in all fields."); return; }
    if (mode === "signup" && !name) { setErr("Please enter your name."); return; }
    if (pw.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (mode === "signup" && !agreed) { setErr("Please agree to the Terms, Privacy Policy, and Medical Disclaimer to continue."); return; }
    setBusy(true);
    try {
      if (mode === "signup") {
        const data = await signUp(email, pw, name, { terms_accepted_at: new Date().toISOString() });
        if (data?.session) {
          const u = { name, email, plan: "free", joinedAt: new Date().toISOString(), id: data.user.id };
          saveUser(u);
          try { if (window.posthog) { window.posthog.identify(u.id, { email, name }); window.posthog.capture("signup_completed"); } } catch(e){}
          trackLead();
          onSuccess(u);
        } else {
          setInfo("Check your email! We sent you a confirmation link. Click it to activate your account, then come back and sign in. Check your spam folder if you don't see it within 2 minutes.");
          setTimeout(() => { setMode("login"); setInfo(""); }, 5000);
          try { window.posthog && window.posthog.capture("signup_pending_confirmation"); } catch(e){}
        }
      } else {
        const data = await signIn(email, pw);
        const meta = data.user.user_metadata || {};
        const u = { name: meta.full_name || email.split("@")[0], email, plan: meta.plan || "free", founding_member: !!meta.founding_member, joinedAt: data.user.created_at, id: data.user.id };
        saveUser(u);
        try { if (window.posthog) { window.posthog.identify(u.id, { email, name: u.name, plan: u.plan }); window.posthog.capture("signin_completed"); } } catch(e){}
        onSuccess(u);
      }
    } catch (e) {
      const msg = (e?.message || "").toLowerCase();
      if (msg.includes("invalid login")) setErr("Wrong email or password.");
      else if (msg.includes("already registered") || msg.includes("user already")) setErr("An account with that email already exists. Try signing in.");
      else if (msg.includes("email") && msg.includes("confirm")) setErr("Please confirm your email first — check your inbox.");
      else setErr(e?.message || "Something went wrong. Please try again.");
    }
    setBusy(false);
  }

  return (
    <Modal onClose={onClose}>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🩺</div>
        <div style={{ fontWeight: 900, fontSize: 22, color: "#fff" }}>{mode === "login" ? "Welcome back" : "Join ShiftMateRN"}</div>
        <div style={{ color: "rgba(255,255,255,.4)", fontSize: 13, marginTop: 4 }}>{mode === "login" ? "Sign in to your account" : "Start free — 5 free sessions"}</div>
      </div>
      {mode === "signup" && <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" style={IS} />}
      <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" type="email" style={IS} />
      <input value={pw} onChange={e => setPw(e.target.value)} placeholder="Password (min 6 chars)" type="password" style={IS} />
      {mode === "signup" && (
        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "rgba(255,255,255,.7)", marginBottom: 14, lineHeight: 1.5, cursor: "pointer" }}>
          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: 2, accentColor: B.pink, cursor: "pointer" }} />
          <span>
            I have read and agree to the{" "}
            <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShowLegal && onShowLegal("terms"); }} style={{ color: B.cyan, cursor: "pointer", textDecoration: "underline" }}>Terms</span>,{" "}
            <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShowLegal && onShowLegal("privacy"); }} style={{ color: B.cyan, cursor: "pointer", textDecoration: "underline" }}>Privacy Policy</span>, and{" "}
            <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShowLegal && onShowLegal("disclaimer"); }} style={{ color: B.cyan, cursor: "pointer", textDecoration: "underline" }}>Medical Disclaimer</span>.
          </span>
        </label>
      )}
      {err && <div style={{ color: B.pink, fontSize: 12, marginBottom: 12 }}>{err}</div>}
      {info && <div style={{ color: B.green, fontSize: 12, marginBottom: 12 }}>{info}</div>}
      <Btn onClick={go} disabled={busy} style={{ width: "100%", marginBottom: 14 }}>{busy ? "Working..." : mode === "login" ? "Sign In" : "Create Free Account"}</Btn>
      <div style={{ textAlign: "center", fontSize: 13, color: "rgba(255,255,255,.4)" }}>
        {mode === "login" ? "No account? " : "Already have one? "}
        <span onClick={() => { setMode(mode === "login" ? "signup" : "login"); setErr(""); setInfo(""); }} style={{ color: B.cyan, cursor: "pointer", fontWeight: 700 }}>
          {mode === "login" ? "Sign up free" : "Sign in"}
        </span>
      </div>
      {mode === "login" && (
        <div style={{ textAlign: "center", marginTop: 10, fontSize: 12 }}>
          <span onClick={async () => {
            if (!email) { setErr("Enter your email above first."); return; }
            setErr(""); setInfo(""); setBusy(true);
            try {
              await resetPasswordForEmail(email);
              setInfo("✅ Reset email sent! Check your inbox and spam folder. Click the link to reset your password then come back and sign in.");
            }
            catch(e) { setErr("Could not send reset email. Try again in a moment."); }
            setBusy(false);
          }} style={{ color: "rgba(255,255,255,.45)", cursor: "pointer", fontWeight: 600 }}>Forgot password?</span>
        </div>
      )}
    </Modal>
  );
}

function DisclaimerGate({ onAccept, onDecline, onShowFull }) {
  const [checks, setChecks] = useState([false, false, false, false]);
  const allChecked = checks.every(Boolean);
  const toggle = i => setChecks(c => c.map((v, idx) => idx === i ? !v : v));
  const BOXES = [
    "I understand that ShiftMateRN is a clinical support and educational tool only. It does NOT replace my clinical judgment, physician orders, NP/PA orders, or my facility's policies and procedures.",
    "I will NOT enter any real patient information — including names, dates of birth, medical record numbers, or any other identifying information. I will use general descriptions only.",
    "I understand that AI can make mistakes. I will verify all clinical information with current resources before acting on it. I will escalate any emergency situation immediately.",
    "I have read and agree to the Terms of Service and Privacy Policy. I am a nurse, nursing student, or healthcare professional using this app for professional development.",
  ];
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.85)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: B.navy2, borderRadius: 20, padding: 32, maxWidth: 520, width: "100%", border: "1px solid rgba(255,255,255,.12)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontWeight: 900, fontSize: 22, color: "#fff", marginBottom: 6 }}>Before you use ShiftMateRN</div>
          <div style={{ color: "rgba(255,255,255,.5)", fontSize: 13 }}>Please read and acknowledge all four statements.</div>
        </div>
        <div style={{ marginBottom: 20 }}>
          {BOXES.map((text, i) => (
            <label key={i} onClick={() => toggle(i)} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 12, color: checks[i] ? "rgba(255,255,255,.9)" : "rgba(255,255,255,.65)", marginBottom: 14, lineHeight: 1.55, cursor: "pointer", background: checks[i] ? "rgba(0,208,132,.06)" : "rgba(255,255,255,.03)", border: `1px solid ${checks[i] ? "rgba(0,208,132,.3)" : "rgba(255,255,255,.08)"}`, borderRadius: 10, padding: "10px 12px", transition: "all .2s" }}>
              <input type="checkbox" checked={checks[i]} onChange={() => {}} style={{ marginTop: 2, accentColor: B.pink, cursor: "pointer", flexShrink: 0 }} />
              <span>{text}</span>
            </label>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <Btn onClick={onDecline} outline color={B.cyan} style={{ flex: 1, padding: "12px", fontSize: 13 }}>Cancel</Btn>
          <Btn onClick={() => allChecked && onAccept()} disabled={!allChecked} color={allChecked ? B.pink : "rgba(255,255,255,.1)"} style={{ flex: 2, padding: "12px", fontSize: 13 }}>I Understand — Enter ShiftMateRN</Btn>
        </div>
        <div style={{ fontSize: 10, color: "rgba(255,255,255,.25)", lineHeight: 1.6, textAlign: "center" }}>
          ShiftMateRN is not a HIPAA-covered entity. Do not enter protected health information. This app is not FDA-approved and is not a medical device. For emergencies always follow your facility's emergency protocols and call 911.
        </div>
      </div>
    </div>
  );
}

function PaywallModal({ onClose, onLogin, context, userEmail }) {
  const isProGate = context === "pro_tool";
  // Same email-prefill logic as the Pricing page — see the Pricing component for
  // the full explanation. Critical for webhook ↔ Supabase account matching.
  const stripeUrlFor = (plan) => {
    if (!plan.stripeLink) return null;
    if (!userEmail) return plan.stripeLink;
    const separator = plan.stripeLink.includes("?") ? "&" : "?";
    return `${plan.stripeLink}${separator}prefilled_email=${encodeURIComponent(userEmail)}`;
  };
  return (
    <Modal onClose={onClose}>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ fontSize: 40, marginBottom: 10 }}>{isProGate ? "🔒" : "⚡"}</div>
        <div style={{ fontWeight: 900, fontSize: 22, color: "#fff", marginBottom: 8 }}>{isProGate ? "EKG Interpreter is a Pro tool" : "You've used all 5 free sessions"}</div>
        <div style={{ color: "rgba(255,255,255,.5)", fontSize: 14, lineHeight: 1.65 }}>{isProGate ? "Upgrade to read any EKG strip from a photo, plus unlimited everything else — less than a coffee per week." : "Upgrade to Pro for unlimited access — $12/month or $97 lifetime founding member price."}</div>
      </div>
      {PLANS.filter(p => p.id !== "free").map(plan => (
        <div key={plan.id} style={{ background: "rgba(255,255,255,.04)", border: `1.5px solid ${plan.color}44`, borderRadius: 16, padding: "18px 20px", marginBottom: 12, position: "relative" }}>
          {plan.badge && <div style={{ position: "absolute", top: -10, right: 16, background: plan.color, color: plan.color === B.yellow ? B.navy : "#fff", fontSize: 10, fontWeight: 800, borderRadius: 20, padding: "3px 12px", letterSpacing: 1 }}>{plan.badge}</div>}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontWeight: 800, color: "#fff", fontSize: 16 }}>{plan.name}</div>
            <div style={{ fontWeight: 900, color: plan.color, fontSize: 20 }}>{plan.price}<span style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,.4)" }}>{plan.period}</span></div>
          </div>
          <ul style={{ listStyle: "none", padding: 0, marginBottom: 14 }}>
            {plan.features.map((f, i) => <li key={i} style={{ fontSize: 12, color: "rgba(255,255,255,.7)", padding: "3px 0" }}>✓ {f}</li>)}
          </ul>
          {userEmail && (
            <div style={{ background: "rgba(245,226,0,.12)", border: "1.5px solid rgba(245,226,0,.5)", borderRadius: 10, padding: "10px 12px", marginBottom: 12, fontSize: 11, color: B.yellow, lineHeight: 1.5, textAlign: "left" }}>
              ⚠️ <strong>USE THIS EMAIL AT CHECKOUT</strong> — pre-filled for you. Changing it = your account won't auto-upgrade.
              <div style={{ marginTop: 4, color: "#fff", fontWeight: 700, wordBreak: "break-all" }}>{userEmail}</div>
            </div>
          )}
          <a href={stripeUrlFor(plan) || plan.stripeLink} target="_blank" rel="noreferrer" style={{ display: "block", textDecoration: "none" }}
            onClick={() => { trackInitiateCheckout(); try { sessionStorage.setItem("rn101_pending_plan", plan.id); } catch(e){} }}>
            <Btn color={plan.color} style={{ width: "100%" }}>{plan.cta}</Btn>
          </a>
        </div>
      ))}
      <div style={{ textAlign: "center", marginTop: 12, fontSize: 12, color: "rgba(255,255,255,.3)" }}>
        Already subscribed? <span onClick={onLogin} style={{ color: B.cyan, cursor: "pointer", fontWeight: 700 }}>Sign in</span>
      </div>
    </Modal>
  );
}

function Pricing({ onBack, userPlan, userEmail }) {
  // Build a Stripe Payment Link URL with the user's email pre-filled.
  // Stripe Payment Links accept ?prefilled_email=... which auto-fills the email
  // field at checkout. Critical because the webhook matches the Stripe purchase
  // to a Supabase account by email — if they enter a different email at Stripe,
  // a separate orphan account gets created instead of upgrading their existing one.
  const stripeUrlFor = (plan) => {
    if (!plan.stripeLink) return null;
    if (!userEmail) return plan.stripeLink;
    const separator = plan.stripeLink.includes("?") ? "&" : "?";
    return `${plan.stripeLink}${separator}prefilled_email=${encodeURIComponent(userEmail)}`;
  };
  return (
    <div style={{ maxWidth: 700, margin: "0 auto", padding: "40px 24px 80px" }}>
      <button onClick={onBack} style={{ background: "transparent", border: "none", color: "rgba(255,255,255,.4)", cursor: "pointer", fontSize: 14, marginBottom: 28, fontFamily: "'Poppins',sans-serif" }}>← Back</button>
      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <div style={{ color: B.pink, fontSize: 11, fontWeight: 800, letterSpacing: 3, textTransform: "uppercase", marginBottom: 10 }}>Simple Pricing</div>
        <h2 style={{ color: "#fff", fontWeight: 900, fontSize: "clamp(26px,5vw,40px)", letterSpacing: -1, marginBottom: 10 }}>Start free. Upgrade when ready.</h2>
        <div style={{ color: "rgba(255,255,255,.4)", fontSize: 15 }}>No tricks. No hidden fees. Cancel anytime.</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 16 }}>
        {PLANS.map(plan => {
          const isCurrent = userPlan === plan.id || (plan.id === "free" && !userPlan);
          const stripeUrl = stripeUrlFor(plan);
          return (
            <div key={plan.id} style={{ background: "rgba(255,255,255,.04)", border: `2px solid ${isCurrent ? plan.color : plan.color + "44"}`, borderRadius: 20, padding: "28px 22px", position: "relative", textAlign: "center" }}>
              {plan.badge && !isCurrent && <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: plan.color, color: plan.color === B.yellow ? B.navy : "#fff", fontSize: 10, fontWeight: 800, borderRadius: 20, padding: "4px 16px", letterSpacing: 1, whiteSpace: "nowrap" }}>{plan.badge}</div>}
              {isCurrent && <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: B.green, color: "#fff", fontSize: 10, fontWeight: 800, borderRadius: 20, padding: "4px 16px", letterSpacing: 1, whiteSpace: "nowrap" }}>✓ Current</div>}
              <div style={{ fontWeight: 900, color: "#fff", fontSize: 18, marginBottom: 6 }}>{plan.name}</div>
              <div style={{ fontWeight: 900, color: plan.color, fontSize: 36, lineHeight: 1, marginBottom: 4 }}>{plan.price}</div>
              <div style={{ color: "rgba(255,255,255,.35)", fontSize: 12, marginBottom: plan.description ? 12 : 20 }}>{plan.period || "forever"}</div>
              {plan.description && <div style={{ color: "rgba(255,255,255,.75)", fontSize: 13, marginBottom: 8, textAlign: "left", lineHeight: 1.55 }}>{plan.description}</div>}
              {plan.spotsCounter && <div style={{ color: plan.color, fontSize: 12, fontWeight: 700, marginBottom: 6, textAlign: "left" }}>{plan.spotsCounter}</div>}
              {plan.urgencyText && <div style={{ color: "rgba(255,255,255,.4)", fontSize: 10, marginBottom: 16, textAlign: "left", lineHeight: 1.45 }}>{plan.urgencyText}</div>}
              <ul style={{ listStyle: "none", padding: 0, marginBottom: 24, textAlign: "left" }}>
                {plan.features.map((f, i) => <li key={i} style={{ fontSize: 12, color: "rgba(255,255,255,.7)", padding: "5px 0", borderBottom: "1px solid rgba(255,255,255,.06)", display: "flex", gap: 8 }}><span style={{ color: plan.color }}>✓</span>{f}</li>)}
              </ul>
              {/* Email-match warning for paid plans — critical so Stripe purchase matches Supabase account */}
              {!isCurrent && plan.stripeLink && userEmail && (
                <div style={{ background: "rgba(245,226,0,.12)", border: "1.5px solid rgba(245,226,0,.5)", borderRadius: 10, padding: "10px 12px", marginBottom: 12, fontSize: 11, color: B.yellow, lineHeight: 1.5, textAlign: "left" }}>
                  ⚠️ <strong>USE THIS EMAIL AT CHECKOUT</strong> — pre-filled for you. Changing it = your account won't auto-upgrade.
                  <div style={{ marginTop: 4, color: "#fff", fontWeight: 700, wordBreak: "break-all" }}>{userEmail}</div>
                </div>
              )}
              {isCurrent
                ? <Btn color={plan.color} disabled style={{ width: "100%" }}>{plan.current}</Btn>
                : stripeUrl
                  ? <a href={stripeUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}
                      onClick={() => { trackInitiateCheckout(); try { sessionStorage.setItem("rn101_pending_plan", plan.id); } catch(e){} }}><Btn color={plan.color} style={{ width: "100%" }}>{plan.cta}</Btn></a>
                  : <Btn color={plan.color} disabled style={{ width: "100%" }}>{plan.cta}</Btn>
              }
            </div>
          );
        })}
      </div>
      <div style={{ textAlign: "center", marginTop: 28, color: "rgba(255,255,255,.25)", fontSize: 12 }}>
        🔒 Secure payment via Stripe · Cancel anytime · rn101hq.com/support
      </div>
    </div>
  );
}

function Saved({ onBack, user }) {
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    let alive = true;
    getSavedResponses(user.id)
      .then(data => { if (alive) { setSaved(data || []); setLoading(false); } })
      .catch(() => { if (alive) { setErr("Could not load your saved responses."); setLoading(false); } });
    return () => { alive = false; };
  }, [user]);

  function copy(id, text) { navigator.clipboard.writeText(text); setCopied(id); setTimeout(() => setCopied(null), 2000); }

  async function handleDelete(id) {
    if (!window.confirm("Delete this saved response?")) return;
    try {
      await deleteSavedResponse(id);
      setSaved(s => s.filter(x => x.id !== id));
    } catch (e) {
      setErr("Could not delete — please try again.");
    }
  }

  return (
    <div style={{ maxWidth: 700, margin: "0 auto", padding: "24px 24px 80px" }}>
      <button onClick={onBack} style={{ background: "transparent", border: "none", color: "rgba(255,255,255,.4)", cursor: "pointer", fontSize: 14, marginBottom: 20, fontFamily: "'Poppins',sans-serif" }}>← Back</button>
      <div style={{ fontWeight: 900, color: "#fff", fontSize: 22, marginBottom: 20 }}>Saved Responses 📚</div>
      {err && <div style={{ color: B.pink, fontSize: 13, marginBottom: 12 }}>{err}</div>}
      {loading
        ? <div style={{ color: "rgba(255,255,255,.4)", textAlign: "center", padding: "60px 0", fontSize: 14 }}>Loading your saved responses...</div>
        : saved.length === 0
          ? <div style={{ color: "rgba(255,255,255,.25)", textAlign: "center", padding: "60px 0", fontSize: 14 }}>Nothing saved yet. Use any tool and click the Save button on a response.</div>
          : saved.map(item => (
            <div key={item.id} style={{ background: "rgba(255,255,255,.04)", border: "1.5px solid rgba(255,255,255,.07)", borderRadius: 14, padding: 20, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ fontWeight: 700, color: "#fff", fontSize: 14 }}>{item.tool_title}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.25)" }}>{new Date(item.created_at).toLocaleDateString()}</div>
              </div>
              {item.question && <div style={{ fontSize: 12, color: "rgba(255,255,255,.4)", marginBottom: 10, fontStyle: "italic", borderLeft: "2px solid rgba(255,255,255,.08)", paddingLeft: 10 }}>"{item.question.slice(0, 100)}{item.question.length > 100 ? "..." : ""}"</div>}
              <div style={{ fontSize: 13, color: "rgba(255,255,255,.82)", lineHeight: 1.75, marginBottom: 12 }}><MarkdownText>{item.response}</MarkdownText></div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => copy(item.id, item.response)} style={ABS}>{copied === item.id ? "✓ Copied" : "📋 Copy"}</button>
                <button onClick={() => openPrint(item.tool_title, item.question, item.response)} style={ABS}>🖨️ Print</button>
                <button onClick={() => handleDelete(item.id)} style={{ ...ABS, color: "rgba(255,80,80,.7)" }}>🗑 Delete</button>
              </div>
            </div>
          ))
      }
    </div>
  );
}

function ResetPassword({ onDone }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  async function go() {
    setErr("");
    if (pw.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (pw !== pw2) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      await updatePassword(pw);
      setDone(true);
      setTimeout(onDone, 2000);
    } catch(e) {
      setErr(e?.message || "Something went wrong. Your reset link may have expired — request a new one from the sign-in screen.");
    }
    setBusy(false);
  }

  return (
    <div style={{ minHeight: "100vh", background: B.navy, fontFamily: "'Poppins',sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: B.navy2, borderRadius: 20, padding: 36, maxWidth: 420, width: "100%", border: "1px solid rgba(255,255,255,.1)" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🔑</div>
          <div style={{ fontWeight: 900, fontSize: 22, color: "#fff", marginBottom: 6 }}>Set a new password</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,.4)" }}>Choose something you'll remember.</div>
        </div>
        {done ? (
          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontWeight: 800, color: B.green, fontSize: 16, marginBottom: 8 }}>Password updated!</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.5)" }}>Taking you to the app...</div>
          </div>
        ) : (
          <>
            <input value={pw} onChange={e => setPw(e.target.value)} placeholder="New password (min 6 chars)" type="password" style={IS} />
            <input value={pw2} onChange={e => setPw2(e.target.value)} placeholder="Confirm new password" type="password" style={{ ...IS, marginBottom: 0 }} onKeyDown={e => e.key === "Enter" && go()} />
            {err && <div style={{ color: B.pink, fontSize: 12, margin: "12px 0" }}>{err}</div>}
            <Btn onClick={go} disabled={busy} style={{ width: "100%", marginTop: 16 }}>{busy ? "Updating..." : "Set new password"}</Btn>
          </>
        )}
      </div>
    </div>
  );
}

function DeleteAccountModal({ user, onClose, onDeleted }) {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const canDelete = confirmText === "DELETE";

  async function handleDelete() {
    if (!canDelete) return;
    setBusy(true); setErr("");
    try {
      await supabase.from("tool_feedback").delete().eq("user_id", user.id);
      await supabase.from("saved_responses").delete().eq("user_id", user.id);
      await supabase.from("profiles").delete().eq("id", user.id);
      await signOut();
      localStorage.clear();
      setDone(true);
      setTimeout(onDeleted, 2500);
    } catch(e) {
      // Fallback: send support email and sign out
      try {
        window.location.href = `mailto:support@rn101hq.com?subject=Account Deletion Request&body=Please delete my account: ${user.email}`;
      } catch(_) {}
      setErr("Could not complete deletion automatically. An email has been opened — send it to support@rn101hq.com and we will process within 24 hours.");
    }
    setBusy(false);
  }

  return (
    <Modal onClose={onClose}>
      {done ? (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
          <div style={{ fontWeight: 800, color: B.green, fontSize: 16, marginBottom: 8 }}>Your account has been deleted.</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,.5)" }}>Thank you for using ShiftMateRN.</div>
        </div>
      ) : (
        <>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🗑</div>
            <div style={{ fontWeight: 900, fontSize: 20, color: "#fff", marginBottom: 8 }}>Delete Your Account</div>
            <div style={{ color: "rgba(255,255,255,.5)", fontSize: 13, lineHeight: 1.6 }}>This will permanently delete your account and all your data. This cannot be undone.</div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,.55)", marginBottom: 8 }}>Type <strong style={{ color: B.pink }}>DELETE</strong> to confirm:</div>
            <input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="Type DELETE here" style={{ ...IS, marginBottom: 0, border: `1.5px solid ${canDelete ? B.pink : "rgba(255,255,255,.12)"}` }} />
          </div>
          {err && <div style={{ color: B.pink, fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>{err}</div>}
          <div style={{ display: "flex", gap: 10 }}>
            <Btn onClick={onClose} outline color={B.cyan} style={{ flex: 1 }}>Cancel</Btn>
            <Btn onClick={handleDelete} disabled={!canDelete || busy} color="#cc2222" style={{ flex: 1 }}>{busy ? "Deleting..." : "Yes, Delete My Account"}</Btn>
          </div>
        </>
      )}
    </Modal>
  );
}

export default function App() {
  const [user, setUserState] = useState(getUser());
  const [usage, setUsage] = useState(getUsage());
  const [screen, setScreen] = useState(() =>
    window.location.pathname === "/reset-password" ? "reset-password" : "home"
  );
  const [toolId, setToolId] = useState(null);
  const [input, setInput] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAuth, setShowAuth] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallContext, setPaywallContext] = useState(null);
  const [pendingToolId, setPendingToolId] = useState(null);
  const [showDisclaimerGate, setShowDisclaimerGate] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [toolMode, setToolMode] = useState(() => {
    const raw = localStorage.getItem("rn101_tool_mode") || "all";
    // Section 2 migration: legacy "student" → "all"; legacy "rn" or "lpn" → merged "rn_lpn"
    if (raw === "student") return "all";
    if (raw === "rn" || raw === "lpn") return "rn_lpn";
    return raw;
  });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const canInstall = !isStandalone();
  const [sharedFlash, setSharedFlash] = useState(null);

  function changeToolMode(m) {
    setToolMode(m);
    try { localStorage.setItem("rn101_tool_mode", m); } catch(e){}
    try { window.posthog && window.posthog.capture("tool_mode_changed", { mode: m }); } catch(e){}
  }

  function completeOnboarding() {
    setShowOnboarding(false);
    try { localStorage.setItem("rn101_onboarded", "true"); } catch(e){}
  }

  const filteredTools = TOOLS.filter(t => {
    if (t.hidden) return false;
    if (toolMode === "all") return true;
    if (toolMode === "rn_lpn") return /nurse/i.test(t.sub) || /student \+ nurse/i.test(t.sub);
    if (toolMode === "ien") return /international/i.test(t.sub) || /student \+ nurse/i.test(t.sub);
    return true;
  }).sort((a, b) => toolOrderIndex(a.id) - toolOrderIndex(b.id));

  const modeMeta = { all: null, rn_lpn: { label: "RN/LPN", color: B.cyan }, ien: { label: "IEN", color: B.cyan } };
  const [copied, setCopied] = useState(null);
  const [savedFlash, setSavedFlash] = useState(null);
  const [pendingImage, setPendingImage] = useState(null);
  const [imgErr, setImgErr] = useState("");
  const [recording, setRecording] = useState(false);
  const [recognitionRef, setRecognitionRef] = useState(null);
  const [speakingId, setSpeakingId] = useState(null);

  function toggleVoiceInput() {
    if (recording && recognitionRef) {
      try { recognitionRef.stop(); } catch(e){}
      setRecognitionRef(null);
      setRecording(false);
      return;
    }
    if (!getSpeechRecognition()) {
      setImgErr("Voice input isn't supported on this browser. Try Chrome or Safari.");
      return;
    }
    setImgErr("");
    const r = startVoiceRecognition(
      (text) => setInput(text),
      () => { setRecording(false); setRecognitionRef(null); },
      () => setImgErr("Microphone error. Check browser permissions and try again.")
    );
    if (r) { setRecognitionRef(r); setRecording(true); }
    try { window.posthog && window.posthog.capture("voice_input_started", { tool_id: tool?.id }); } catch(e){}
  }

  function toggleSpeak(id, text) {
    if (speakingId === id) {
      stopSpeaking();
      setSpeakingId(null);
      return;
    }
    stopSpeaking();
    setSpeakingId(id);
    speakResponse(text, () => setSpeakingId(null));
    try { window.posthog && window.posthog.capture("voice_output_played", { tool_id: tool?.id }); } catch(e){}
  }

  useEffect(() => { setUsage(getUsage()); }, []);
  useEffect(() => { trackPageView(); }, [screen]);

  useEffect(() => {
    let alive = true;
    async function loadUser() {
      try {
        const supaUser = await getCurrentUser();
        if (!alive || !supaUser) return;
        const meta = supaUser.user_metadata || {};
        const u = {
          name: meta.full_name || supaUser.email.split("@")[0],
          email: supaUser.email,
          plan: meta.plan || "free",
          founding_member: !!meta.founding_member,
          joinedAt: supaUser.created_at,
          id: supaUser.id,
        };
        saveUser(u); setUserState(u);
      } catch (e) {}
    }
    loadUser();
    // If we just got redirected back from Stripe, re-fetch the user to pick up plan upgrade
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgraded") === "true") {
      setTimeout(loadUser, 1500); // give the webhook a moment to land
      setTimeout(loadUser, 4000); // and again, in case it's slower
      window.history.replaceState({}, "", window.location.pathname);
      try { window.posthog && window.posthog.capture("upgrade_completed"); } catch(e){}
      try {
        const planPrices = { pro: 12, bundle: 97 };
        const pendingPlan = sessionStorage.getItem("rn101_pending_plan");
        trackPurchase(planPrices[pendingPlan] ?? 12, "USD");
        sessionStorage.removeItem("rn101_pending_plan");
      } catch(e) {}
    }
    const { data: sub } = onAuthChange((event, supaUser) => {
      if (!alive) return;
      if (event === "SIGNED_OUT" || !supaUser) { nukeUser(); setUserState(null); }
      else if (event === "PASSWORD_RECOVERY") { setScreen("reset-password"); }
    });
    return () => { alive = false; sub?.subscription?.unsubscribe?.(); };
  }, []);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    const installed = () => { setInstallPrompt(null); setShowInstallModal(false); };
    window.addEventListener("appinstalled", installed);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  async function triggerInstall() {
    if (!installPrompt) return;
    try { installPrompt.prompt(); await installPrompt.userChoice; } catch (e) {}
    setInstallPrompt(null);
    setShowInstallModal(false);
  }

  const tool = TOOLS.find(t => t.id === toolId);
  const isPro = user?.plan === "pro" || user?.plan === "bundle" || user?.founding_member === true;
  const sessLeft = Math.max(0, FREE_SESSION_LIMIT - usage.count);

  function login(u) {
    setUserState(u);
    saveUser(u);
    setShowAuth(null);
    if (!localStorage.getItem("rn101_onboarded")) setShowOnboarding(true);
  }
  async function logout() { try { await signOut(); } catch(e){} nukeUser(); setUserState(null); }
  function openTool(id) {
    const t = TOOLS.find(x => x.id === id);
    if (t?.proOnly && !isPro) {
      setPaywallContext("pro_tool");
      setShowPaywall(true);
      try { window.posthog && window.posthog.capture("pro_gate_hit", { tool_id: id }); } catch(e){}
      return;
    }
    // Disclaimer gate — required acknowledgment before first tool use
    // Signed-in users have already accepted at signup (terms_accepted_at in metadata)
    if (!user && !getDisclaimerAccepted()) {
      setPendingToolId(id);
      setShowDisclaimerGate(true);
      return;
    }
    setToolId(id); setHistory([]); setInput(""); setError(""); setScreen("tool");
    try { window.posthog && window.posthog.capture("tool_opened", { tool_id: id }); } catch(e){}
  }

  function acceptDisclaimerAndOpen() {
    setDisclaimerAccepted();
    setShowDisclaimerGate(false);
    const id = pendingToolId;
    setPendingToolId(null);
    if (id) {
      setToolId(id); setHistory([]); setInput(""); setError(""); setScreen("tool");
      try { window.posthog && window.posthog.capture("disclaimer_accepted_first_use", { tool_id: id }); } catch(e){}
      try { window.posthog && window.posthog.capture("tool_opened", { tool_id: id }); } catch(e){}
    }
  }

  async function submit() {
    if ((!input.trim() && !pendingImage) || !tool) return;
    if (!isPro && usage.count >= FREE_SESSION_LIMIT) { setPaywallContext("session_limit"); setShowPaywall(true); return; }
    const msg = input;
    const img = pendingImage;
    setInput(""); setPendingImage(null); setLoading(true); setError("");
    // Snapshot the history at the moment of submit (before we add the new user turn)
    const priorHistory = history;
    setHistory(h => [...h, { role: "user", content: msg, image: img }]);
    const c = addUsage();
    setUsage(u => ({ ...u, count: c }));
    try {
      const reply = await callAI(buildSystemPrompt(tool, toolMode), priorHistory, msg, img, tool.id);
      setHistory(h => [...h, { role: "assistant", content: reply, question: msg }]);
      try { window.posthog && window.posthog.capture("message_sent", { tool_id: tool.id, has_image: !!img, is_pro: !!isPro }); } catch(e){}
    } catch (e) {
      const msg2 = (e?.message || "").toLowerCase();
      if (msg2.includes("429") || msg2.includes("rate")) setError("You are moving fast! Give it a second and try again 🔄");
      else if (msg2.includes("timeout") || msg2.includes("408")) setError("That took too long — please try a shorter question or try again.");
      else if (msg2.includes("network") || msg2.includes("fetch")) setError("Connection hiccup — please try again in a moment 🔄");
      else setError("Something went sideways — please try again. If this keeps happening contact support@rn101hq.com");
      try { window.posthog && window.posthog.capture("message_failed", { tool_id: tool.id }); } catch(e){}
    }
    setLoading(false);
  }

  async function handleImageFile(file) {
    setImgErr("");
    if (!file) return;
    if (!file.type.startsWith("image/")) { setImgErr("Please pick an image file."); return; }
    if (file.size > 12 * 1024 * 1024) { setImgErr("Image too large — please pick one under 12 MB."); return; }
    try {
      const img = await fileToCompressedImage(file);
      setPendingImage(img);
    } catch (e) {
      setImgErr("Couldn't read that image. Try a different one.");
    }
  }

  function copyText(id, text) { navigator.clipboard.writeText(text); setCopied(id); setTimeout(() => setCopied(null), 2000); }

  async function shareMsg(id, text) {
    if (navigator.share) {
      try { await navigator.share({ text }); setSharedFlash(id); setTimeout(() => setSharedFlash(null), 2000); } catch(e) {}
    } else {
      navigator.clipboard.writeText(text);
      setSharedFlash(id);
      setTimeout(() => setSharedFlash(null), 2000);
    }
  }
  async function saveMsg(id, msg) {
    if (!user) { setShowAuth("login"); return; }
    try {
      await saveResponse({ userId: user.id, toolId: tool.id, toolTitle: tool.title, question: msg.question, response: msg.content });
      setSavedFlash(id);
      setTimeout(() => setSavedFlash(null), 2000);
      try { window.posthog && window.posthog.capture("response_saved", { tool_id: tool.id }); } catch(e){}
    } catch (e) {
      setError("Could not save — please try again.");
    }
  }

  const Nav = () => (
    <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(255,255,255,.07)", background: B.navy, position: "sticky", top: 0, zIndex: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => setScreen("home")}>
        <div style={{ display: "flex", gap: 4 }}>
          {[B.pink, B.yellow, B.cyan, B.green].map((c, i) => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: c }} />)}
        </div>
        <span style={{ fontWeight: 900, fontSize: 17, color: "#fff", letterSpacing: -0.5 }}>Shift<span style={{ color: B.pink }}>Mate</span><span style={{ color: B.cyan }}>RN</span></span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {modeMeta[toolMode] && <div style={{ background: modeMeta[toolMode].color + "22", border: `1px solid ${modeMeta[toolMode].color}55`, borderRadius: 20, padding: "3px 10px", fontSize: 10, fontWeight: 800, color: modeMeta[toolMode].color, letterSpacing: 1 }}>{modeMeta[toolMode].label}</div>}
        {!isPro && (
          <div title={sessLeft <= 3 && sessLeft > 0 ? `${sessLeft} sessions left — upgrade to Pro for unlimited access` : undefined}
            style={{ fontSize: 11, fontWeight: 700, color: sessLeft === 0 ? B.pink : sessLeft === 1 ? "#FF6600" : sessLeft <= 3 ? B.yellow : "rgba(255,255,255,.3)", cursor: sessLeft <= 3 ? "help" : "default", transition: "color .3s" }}>
            {sessLeft} free left
          </div>
        )}
        {user ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,.55)", fontWeight: 600 }}>Hi, {(user.name || "").split(" ")[0] || "there"}</div>
            {canInstall && <Btn onClick={() => setShowInstallModal(true)} outline color={B.green} style={{ padding: "7px 14px", fontSize: 12 }}>📲 Install</Btn>}
            <Btn onClick={() => setScreen("saved")} outline color={B.cyan} style={{ padding: "7px 14px", fontSize: 12 }}>Saved</Btn>
            <Btn onClick={() => setScreen("pricing")} color={B.pink} style={{ padding: "7px 14px", fontSize: 12 }}>{(user?.plan === "bundle" || user?.founding_member === true) ? "✓ Lifetime" : isPro ? "✓ Pro" : "Upgrade"}</Btn>
            <div onClick={logout} style={{ cursor: "pointer", fontSize: 11, color: "rgba(255,255,255,.25)", fontWeight: 700 }}>Sign out</div>
            <div onClick={() => setShowDeleteAccount(true)} style={{ cursor: "pointer", fontSize: 10, color: "rgba(255,80,80,.35)", fontWeight: 600 }}>Delete account</div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8 }}>
            {canInstall && <Btn onClick={() => setShowInstallModal(true)} outline color={B.green} style={{ padding: "7px 14px", fontSize: 12 }}>📲 Install</Btn>}
            <Btn onClick={() => setShowAuth("login")} outline color={B.cyan} style={{ padding: "7px 14px", fontSize: 12 }}>Sign In</Btn>
            <Btn onClick={() => setScreen("pricing")} color={B.pink} style={{ padding: "7px 14px", fontSize: 12 }}>Go Pro</Btn>
          </div>
        )}
      </div>
    </div>
  );

  const Modals = () => (
    <>
      {showAuth && <AuthModal mode={showAuth} onClose={() => setShowAuth(null)} onSuccess={login} onShowLegal={(s) => { setShowAuth(null); setScreen(s); }} />}
      {showPaywall && <PaywallModal context={paywallContext} userEmail={user?.email} onClose={() => setShowPaywall(false)} onLogin={() => { setShowPaywall(false); setShowAuth("login"); }} />}
      {showDisclaimerGate && <DisclaimerGate onAccept={acceptDisclaimerAndOpen} onDecline={() => { setShowDisclaimerGate(false); setPendingToolId(null); }} onShowFull={() => { setShowDisclaimerGate(false); setPendingToolId(null); setScreen("disclaimer"); }} />}
      {showDeleteAccount && user && <DeleteAccountModal user={user} onClose={() => setShowDeleteAccount(false)} onDeleted={() => { setShowDeleteAccount(false); setUserState(null); setScreen("home"); }} />}
      {showInstallModal && <InstallInstructionsModal onClose={() => setShowInstallModal(false)} installPrompt={installPrompt} onTriggerInstall={triggerInstall} />}
      {showOnboarding && <OnboardingFlow onComplete={completeOnboarding} onSkip={completeOnboarding} onShowInstall={() => setShowInstallModal(true)} />}
      {!user && <SocialProofTicker />}
    </>
  );

  const CSS = () => (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800;900&display=swap');
      * { box-sizing: border-box; }
      @keyframes bounce { 0%,80%,100%{transform:scale(0)} 40%{transform:scale(1)} }
      @keyframes pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(255,45,120,.7); } 50% { box-shadow: 0 0 0 8px rgba(255,45,120,0); } }
      ::-webkit-scrollbar { width: 4px; }
      ::-webkit-scrollbar-thumb { background: rgba(255,255,255,.1); border-radius: 4px; }
      textarea::placeholder, input::placeholder { color: rgba(255,255,255,.25); }
    `}</style>
  );

  const Foot = () => (
    <div style={{ borderTop: "1px solid rgba(255,255,255,.06)", padding: "24px 20px 32px", textAlign: "center", color: "rgba(255,255,255,.35)", fontSize: 12, lineHeight: 1.8 }}>
      <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <span onClick={() => setScreen("howto")} style={{ cursor: "pointer", color: "rgba(255,255,255,.55)" }}>How it works</span>
        <span onClick={() => setScreen("faq")} style={{ cursor: "pointer", color: "rgba(255,255,255,.55)" }}>FAQ</span>
        <span onClick={() => setScreen("privacy")} style={{ cursor: "pointer", color: "rgba(255,255,255,.55)" }}>Privacy</span>
        <span onClick={() => setScreen("terms")} style={{ cursor: "pointer", color: "rgba(255,255,255,.55)" }}>Terms</span>
        <span onClick={() => setScreen("disclaimer")} style={{ cursor: "pointer", color: "rgba(255,255,255,.55)" }}>Medical Disclaimer</span>
        <a href="mailto:support@rn101hq.com" style={{ color: "rgba(255,255,255,.55)", textDecoration: "none" }}>Support</a>
      </div>
      <div>© {new Date().getFullYear()} Shift<span style={{ color: B.pink }}>Mate</span><span style={{ color: B.cyan }}>RN</span> · Educational tool, not medical advice.</div>
    </div>
  );

  if (screen === "reset-password") return <ResetPassword onDone={async () => {
    window.history.replaceState({}, "", "/");
    try {
      const supaUser = await getCurrentUser();
      if (supaUser) {
        const meta = supaUser.user_metadata || {};
        const u = { name: meta.full_name || supaUser.email.split("@")[0], email: supaUser.email, plan: meta.plan || "free", founding_member: !!meta.founding_member, joinedAt: supaUser.created_at, id: supaUser.id };
        saveUser(u); setUserState(u);
      }
    } catch(e) {}
    setScreen("home");
  }} />;

  if (screen === "pricing")    return <div style={{ minHeight: "100vh", background: B.navy, fontFamily: "'Poppins',sans-serif" }}><Nav /><Pricing onBack={() => setScreen("home")} userPlan={user?.plan} userEmail={user?.email} /><Foot /><Modals /><CSS /></div>;
  if (screen === "saved")      return <div style={{ minHeight: "100vh", background: B.navy, fontFamily: "'Poppins',sans-serif" }}><Nav /><Saved onBack={() => setScreen("home")} user={user} /><Foot /><Modals /><CSS /></div>;
  if (screen === "privacy")    return <div style={{ minHeight: "100vh", background: B.navy, fontFamily: "'Poppins',sans-serif" }}><Nav /><PrivacyPolicy onBack={() => setScreen("home")} /><Foot /><Modals /><CSS /></div>;
  if (screen === "terms")      return <div style={{ minHeight: "100vh", background: B.navy, fontFamily: "'Poppins',sans-serif" }}><Nav /><TermsOfService onBack={() => setScreen("home")} /><Foot /><Modals /><CSS /></div>;
  if (screen === "disclaimer") return <div style={{ minHeight: "100vh", background: B.navy, fontFamily: "'Poppins',sans-serif" }}><Nav /><Disclaimer onBack={() => setScreen("home")} /><Foot /><Modals /><CSS /></div>;
  if (screen === "howto")      return <div style={{ minHeight: "100vh", background: B.navy, fontFamily: "'Poppins',sans-serif" }}><Nav /><HowToUse onBack={() => setScreen("home")} onGoHome={() => setScreen("home")} /><Foot /><Modals /><CSS /></div>;
  if (screen === "faq")        return <div style={{ minHeight: "100vh", background: B.navy, fontFamily: "'Poppins',sans-serif" }}><Nav /><FAQ onBack={() => setScreen("home")} onShowDisclaimer={() => setScreen("disclaimer")} onShowTerms={() => setScreen("terms")} onShowPrivacy={() => setScreen("privacy")} /><Foot /><Modals /><CSS /></div>;

  if (screen === "home") return (
    <div style={{ minHeight: "100vh", background: B.navy, fontFamily: "'Poppins',sans-serif" }}>
      <Nav />

      {/* HERO — landing only (hidden for signed-in users so the app view is clean) */}
      {!user && (
        <div style={{ padding: "70px 24px 60px", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 30% 30%, rgba(255,45,120,.14), transparent 50%), radial-gradient(circle at 70% 60%, rgba(0,200,232,.10), transparent 50%)", pointerEvents: "none" }} />
          <div style={{ position: "relative", maxWidth: 920, margin: "0 auto", textAlign: "center" }}>
            <div style={{ display: "inline-block", background: "rgba(0,200,232,.1)", border: "1px solid rgba(0,200,232,.3)", borderRadius: 50, padding: "6px 18px", fontSize: 11, fontWeight: 800, color: B.cyan, letterSpacing: 2, textTransform: "uppercase", marginBottom: 22 }}>Built by Adie · 19-year cardiac RN</div>
            <h1 style={{ fontSize: "clamp(34px,7.5vw,64px)", fontWeight: 900, color: "#fff", lineHeight: 1.02, letterSpacing: -2, marginBottom: 22 }}>What if you never had to face <span style={{ color: B.yellow }}>the floor alone</span> again?</h1>
            <p style={{ color: "rgba(255,255,255,.7)", fontSize: 18, maxWidth: 620, margin: "0 auto 32px", lineHeight: 1.6 }}>ShiftMateRN is your AI nursing coach in your pocket. 24 tools that turn the worst moments of your shift into 90-second answers. Built by a nurse who's been in your scrubs.</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <Btn onClick={() => setShowAuth("signup")} color={B.pink} style={{ padding: "14px 32px", fontSize: 15 }}>Give me my 5 free sessions →</Btn>
            </div>
            <div style={{ marginTop: 20, fontSize: 12, color: "rgba(255,255,255,.4)" }}>5 free sessions · cancel anytime · less than a Friday coffee for Pro</div>
          </div>
        </div>
      )}

      {/* WE SEE YOU — FEEL SEEN SECTION */}
      {!user && (
        <div style={{ padding: "50px 20px 30px", maxWidth: 1080, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div style={{ color: B.pink, fontSize: 11, fontWeight: 800, letterSpacing: 3, textTransform: "uppercase", marginBottom: 12 }}>This is for you</div>
            <h2 style={{ color: "#fff", fontWeight: 900, fontSize: "clamp(28px,5.5vw,42px)", letterSpacing: -1.2, lineHeight: 1.1, marginBottom: 14 }}>We see you.</h2>
            <p style={{ color: "rgba(255,255,255,.55)", fontSize: 15, maxWidth: 560, margin: "0 auto", lineHeight: 1.7 }}>If you've ever felt any of this — the 3am dread, the imposter syndrome, the bathroom tears — you're not weak, you're just nursing without enough support. ShiftMateRN exists for exactly these moments.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 16 }}>
            {[
              { tag: "THE NEW GRAD", color: B.pink, q: "You — the new RN who just got your license and is convinced one wrong move ends your career. You replay every shift on the drive home." },
              { tag: "THE INTERNATIONAL NURSE", color: B.cyan, q: "You — the nurse who passed boards in your home country, then walked into an American hospital where every chart is in shorthand nobody taught you." },
              { tag: "THE FIRST CODE", color: B.red, q: "You — six weeks off orientation, watching your first code unfold and realizing your hands aren't moving fast enough because you've never actually done this part." },
              { tag: "THE HEAVY ASSIGNMENT", color: B.green, q: "You — eight months in, walking into a six-patient assignment that should never have been yours, knowing you can't say no without paying for it tomorrow." },
            ].map((p, i) => (
              <div key={i} style={{ background: "rgba(255,255,255,.04)", border: `1.5px solid ${p.color}33`, borderRadius: 16, padding: "22px 24px" }}>
                <div style={{ display: "inline-block", background: p.color + "22", border: `1px solid ${p.color}44`, borderRadius: 20, padding: "3px 11px", fontSize: 9, fontWeight: 800, color: p.color, letterSpacing: 1.5, marginBottom: 14 }}>{p.tag}</div>
                <div style={{ fontSize: 14, color: "rgba(255,255,255,.85)", lineHeight: 1.7, fontStyle: "italic" }}>"{p.q}"</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* THE TRANSFORMATION — BEFORE / AFTER */}
      {!user && (
        <div style={{ padding: "70px 20px 30px", maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div style={{ color: B.cyan, fontSize: 11, fontWeight: 800, letterSpacing: 3, textTransform: "uppercase", marginBottom: 12 }}>What changes when you have ShiftMateRN</div>
            <h2 style={{ color: "#fff", fontWeight: 900, fontSize: "clamp(28px,5.5vw,42px)", letterSpacing: -1.2, lineHeight: 1.1 }}>Your shift, before vs. after.</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 900, margin: "0 auto" }}>
            <div style={{ textAlign: "center", color: "rgba(255,255,255,.4)", fontSize: 11, fontWeight: 800, letterSpacing: 2, padding: "8px 0" }}>BEFORE</div>
            <div style={{ textAlign: "center", color: B.green, fontSize: 11, fontWeight: 800, letterSpacing: 2, padding: "8px 0" }}>AFTER</div>
            {[
              ["Crying in your car after a hard shift", "Type how you feel into Burnout Check-In, get warmth + practical coping in 30 seconds"],
              ["Googling 'how to write a fall note' between rooms", "Type rough notes → polished, defensible documentation in 30 seconds"],
              ["Texting a nurse friend 'is this rhythm normal??'", "Snap a photo of the strip → instant read with action steps"],
              ["Cramming flashcards alone for NCLEX", "NGN-style questions with a tutor explaining the reasoning"],
              ["Hesitating to call the doctor at 3am", "Practice the SBAR with an AI attending who pushes back, so the real call is easy"],
              ["Memorizing drug interactions you'll never recall at the bedside", "Ask Med Safety Check the second you need it"],
            ].map(([before, after], i) => (
              <React.Fragment key={i}>
                <div style={{ background: "rgba(255,45,120,.06)", border: "1px solid rgba(255,45,120,.18)", borderRadius: 12, padding: "14px 18px", fontSize: 13, color: "rgba(255,255,255,.7)", lineHeight: 1.6 }}>{before}</div>
                <div style={{ background: "rgba(0,208,132,.08)", border: "1px solid rgba(0,208,132,.3)", borderRadius: 12, padding: "14px 18px", fontSize: 13, color: "#fff", lineHeight: 1.6, fontWeight: 600 }}>{after}</div>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* FEATURE SPOTLIGHT — EKG PHOTO READS */}
      {!user && (
        <div style={{ padding: "70px 20px", background: "linear-gradient(135deg, rgba(255,45,120,.08), rgba(245,226,0,.04))", borderTop: "1px solid rgba(255,255,255,.05)", borderBottom: "1px solid rgba(255,255,255,.05)" }}>
          <div style={{ maxWidth: 920, margin: "0 auto", textAlign: "center" }}>
            <div style={{ color: B.pink, fontSize: 11, fontWeight: 800, letterSpacing: 3, textTransform: "uppercase", marginBottom: 14 }}>The feature that breaks brains</div>
            <h2 style={{ color: "#fff", fontWeight: 900, fontSize: "clamp(28px,5.5vw,46px)", letterSpacing: -1.5, lineHeight: 1.1, marginBottom: 18 }}>Snap a photo of any EKG strip.<br /><span style={{ color: B.yellow }}>Get a real read in 30 seconds.</span></h2>
            <p style={{ color: "rgba(255,255,255,.7)", fontSize: 16, maxWidth: 620, margin: "0 auto 24px", lineHeight: 1.7 }}>Paper strip, monitor screen, textbook page — point your phone, send it. Our AI tells you the rhythm, the rate, why it's clinically significant, and exactly what to do next. No more flipping textbooks at 3am. No more "I think it's afib but I'm not sure."</p>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 12, background: "rgba(0,0,0,.3)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 50, padding: "8px 18px", fontSize: 12, color: "rgba(255,255,255,.65)", marginBottom: 18 }}>
              <span style={{ color: B.pink, fontWeight: 800 }}>📸 → 🧠 → 🩺</span>
              <span>Photo. AI. Action steps.</span>
            </div>
            <div style={{ marginTop: 16 }}>
              <div style={{ display: "inline-block", background: B.pink + "22", color: B.pink, border: `1px solid ${B.pink}66`, borderRadius: 20, padding: "5px 14px", fontSize: 11, fontWeight: 800, letterSpacing: 1.5 }}>🔒 PRO FEATURE — $12/MO</div>
            </div>
          </div>
        </div>
      )}

      {/* HOW IT WORKS */}
      {!user && (
        <div style={{ padding: "60px 20px 20px", maxWidth: 1080, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <div style={{ color: B.cyan, fontSize: 11, fontWeight: 800, letterSpacing: 3, textTransform: "uppercase", marginBottom: 10 }}>How it works</div>
            <h2 style={{ color: "#fff", fontWeight: 900, fontSize: "clamp(24px,5vw,34px)", letterSpacing: -1 }}>Three steps. 30 seconds.</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 18 }}>
            {[
              { n: "1", c: B.pink, t: "Pick a tool", d: "Charting, EKG photo reads, NCLEX, drug safety, burnout check-ins, 13 more." },
              { n: "2", c: B.cyan, t: "Ask or snap", d: "Type your question, paste rough notes, or attach a photo. AI tuned for nurses, not for general chat." },
              { n: "3", c: B.green, t: "Save it forever", d: "Bookmark answers. Print for shift reports. Library follows you across every device you use." },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{ width: 60, height: 60, borderRadius: 16, background: s.c, color: "#fff", fontWeight: 900, fontSize: 24, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>{s.n}</div>
                <div style={{ fontWeight: 800, color: "#fff", fontSize: 16, marginBottom: 8 }}>{s.t}</div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,.55)", lineHeight: 1.7, maxWidth: 280, margin: "0 auto" }}>{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TOOLS GRID — preview for visitors (click-to-signup), full dashboard for signed-in users */}
      <div style={{ padding: "60px 20px 30px", maxWidth: 1100, margin: "0 auto" }}>
        {/* Heading: sales pitch for visitors; signed-in users skip straight to the toolkit */}
        {!user && (
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ color: B.pink, fontSize: 11, fontWeight: 800, letterSpacing: 3, textTransform: "uppercase", marginBottom: 10 }}>The toolkit</div>
            <h2 style={{ color: "#fff", fontWeight: 900, fontSize: "clamp(26px,5vw,38px)", letterSpacing: -1, marginBottom: 12 }}>24 tools. One app.<br /><span style={{ color: B.yellow }}>Zero judgment.</span></h2>
            <p style={{ color: "rgba(255,255,255,.5)", fontSize: 13, maxWidth: 520, margin: "0 auto" }}>Preview every tool below. Click any card to sign up and start using it.</p>
          </div>
        )}

        {/* Mode toggle — only useful once you can actually use the tools */}
        {user && (
          <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 24, flexWrap: "wrap" }}>
            {[
              { id: "all", label: "All tools", icon: "✨" },
              { id: "rn_lpn", label: "RN/LPN", icon: "🩺" },
              { id: "ien", label: "IEN", icon: "🌍" },
            ].map(m => (
              <button
                key={m.id}
                onClick={() => changeToolMode(m.id)}
                style={{
                  background: toolMode === m.id ? B.pink : "rgba(255,255,255,.04)",
                  color: toolMode === m.id ? "#fff" : "rgba(255,255,255,.65)",
                  border: `1.5px solid ${toolMode === m.id ? B.pink : "rgba(255,255,255,.1)"}`,
                  borderRadius: 24,
                  padding: "9px 18px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "'Poppins',sans-serif",
                  transition: "all .15s",
                  letterSpacing: 0.5,
                }}
              >{m.icon} {m.label}</button>
            ))}
          </div>
        )}

        {/* Session warning banners — only for signed-in free users with sessions left */}
        {user && !isPro && sessLeft === 1 && (
          <div style={{ background: "rgba(255,102,0,.12)", border: "1px solid rgba(255,102,0,.4)", borderRadius: 10, padding: "10px 16px", marginBottom: 16, textAlign: "center", fontSize: 13, color: "#FF6600", fontWeight: 700 }}>
            Last free session! Upgrade to Pro — less than a Friday coffee ☕ <span onClick={() => setScreen("pricing")} style={{ color: B.cyan, cursor: "pointer", textDecoration: "underline", marginLeft: 6 }}>Upgrade →</span>
          </div>
        )}
        {user && !isPro && sessLeft <= 3 && sessLeft > 1 && (
          <div style={{ background: "rgba(245,226,0,.08)", border: "1px solid rgba(245,226,0,.3)", borderRadius: 10, padding: "10px 16px", marginBottom: 16, textAlign: "center", fontSize: 13, color: B.yellow, fontWeight: 600 }}>
            {sessLeft} sessions left — <span onClick={() => setScreen("pricing")} style={{ color: B.cyan, cursor: "pointer", textDecoration: "underline" }}>upgrade to Pro for unlimited access →</span>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 14 }}>
          {filteredTools.map(t => (
            <button key={t.id} onClick={() => user ? openTool(t.id) : setShowAuth("signup")} title={t.desc}
              style={{ background: "rgba(255,255,255,.04)", border: "1.5px solid rgba(255,255,255,.08)", borderRadius: 16, padding: "20px 22px", textAlign: "left", cursor: "pointer", transition: "all .2s", display: "flex", flexDirection: "column", gap: 10, fontFamily: "'Poppins',sans-serif" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = t.color + "66"; e.currentTarget.style.background = "rgba(255,255,255,.07)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,.08)"; e.currentTarget.style.background = "rgba(255,255,255,.04)"; }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ width: 40, height: 40, borderRadius: 11, background: t.color + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{t.icon}</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {t.proOnly && !isPro && <div style={{ background: B.pink, color: "#fff", borderRadius: 20, padding: "3px 9px", fontSize: 9, fontWeight: 800, letterSpacing: 1 }}>🔒 PRO</div>}
                  <div style={{ background: t.color + "22", border: `1px solid ${t.color}44`, borderRadius: 20, padding: "3px 10px", fontSize: 10, fontWeight: 700, color: t.color, letterSpacing: 1 }}>{t.sub}</div>
                </div>
              </div>
              <div style={{ fontWeight: 800, color: "#fff", fontSize: 14 }}>{t.title}</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,.5)", lineHeight: 1.6 }}>{t.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* VOICE SPOTLIGHT */}
      {!user && (
        <div style={{ padding: "70px 20px", maxWidth: 920, margin: "0 auto", textAlign: "center" }}>
          <div style={{ color: B.cyan, fontSize: 11, fontWeight: 800, letterSpacing: 3, textTransform: "uppercase", marginBottom: 14 }}>For when your hands are full</div>
          <h2 style={{ color: "#fff", fontWeight: 900, fontSize: "clamp(28px,5.5vw,44px)", letterSpacing: -1.5, lineHeight: 1.1, marginBottom: 18 }}>Tap the mic. <span style={{ color: B.cyan }}>Just talk.</span></h2>
          <p style={{ color: "rgba(255,255,255,.7)", fontSize: 16, maxWidth: 600, margin: "0 auto 24px", lineHeight: 1.7 }}>Mid-shift, walking between rooms, can't type with gloves on? Hit the 🎙 button and dictate. Your words appear live in the chat. Edit anything, hit send. The AI can also <strong style={{ color: "#fff" }}>read its answer back to you</strong> — perfect for hands-free shift prep or when you're driving home processing a tough day.</p>
          <div style={{ display: "inline-flex", gap: 8, alignItems: "center", background: "rgba(0,0,0,.3)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 50, padding: "8px 18px", fontSize: 12, color: "rgba(255,255,255,.65)" }}>
            <span style={{ color: B.cyan, fontWeight: 800 }}>🎙 ↔ 🔊</span>
            <span>Voice in. Voice out. Free for everyone.</span>
          </div>
        </div>
      )}

      {/* FOUNDER BLOCK */}
      {!user && (
        <div style={{ padding: "70px 20px", background: "rgba(255,45,120,.04)", borderTop: "1px solid rgba(255,255,255,.05)", borderBottom: "1px solid rgba(255,255,255,.05)" }}>
          <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
            <div style={{ color: B.pink, fontSize: 11, fontWeight: 800, letterSpacing: 3, textTransform: "uppercase", marginBottom: 14 }}>Built by a nurse, for nurses</div>
            <h2 style={{ color: "#fff", fontWeight: 900, fontSize: "clamp(22px,4.5vw,32px)", letterSpacing: -0.8, lineHeight: 1.3, marginBottom: 20 }}>Hi, I'm Adie.</h2>
            <p style={{ color: "rgba(255,255,255,.78)", fontSize: 15, lineHeight: 1.8, marginBottom: 14 }}>
              19 years on the cardiac floor. I've been the new grad who couldn't sleep before her first solo shift. The traveler who walked into a hospital with a 90-second handoff and zero idea where the supply room was. The preceptor who watched her trainee freeze during a code.
            </p>
            <p style={{ color: "rgba(255,255,255,.78)", fontSize: 15, lineHeight: 1.8, marginBottom: 14 }}>
              I built ShiftMateRN because nobody should walk into nursing alone — not in 2026. Not when AI exists. Not when there's a 19-year veteran out here who'd give anything to be the Big Sis you couldn't find on your unit.
            </p>
            <p style={{ color: "#fff", fontSize: 16, lineHeight: 1.7, marginBottom: 22, fontWeight: 700 }}>
              This is the app I wish I had when I was you.
            </p>
            <div style={{ fontSize: 13, color: B.pink, fontWeight: 700, fontStyle: "italic" }}>— Adie, RN · Founder, ShiftMateRN · 19 years cardiac</div>
          </div>
        </div>
      )}

      {/* PRICING SUMMARY */}
      {!user && (
        <div style={{ padding: "60px 20px 30px", maxWidth: 980, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ color: B.cyan, fontSize: 11, fontWeight: 800, letterSpacing: 3, textTransform: "uppercase", marginBottom: 10 }}>Simple pricing</div>
            <h2 style={{ color: "#fff", fontWeight: 900, fontSize: "clamp(24px,5vw,34px)", letterSpacing: -1 }}>Start free. Upgrade when ready.</h2>
            <div style={{ color: "rgba(255,255,255,.4)", fontSize: 14, marginTop: 8 }}>No tricks. No hidden fees. Less than a Friday coffee.</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14 }}>
            {PLANS.map(p => (
              <div key={p.id} style={{ background: "rgba(255,255,255,.04)", border: `2px solid ${p.color}44`, borderRadius: 18, padding: "22px 20px", textAlign: "center", position: "relative" }}>
                {p.badge && <div style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)", background: p.color, color: p.color === B.yellow ? B.navy : "#fff", fontSize: 9, fontWeight: 900, borderRadius: 20, padding: "3px 12px", letterSpacing: 1, whiteSpace: "nowrap" }}>{p.badge}</div>}
                <div style={{ fontWeight: 900, color: "#fff", fontSize: 16, marginBottom: 4 }}>{p.name}</div>
                <div style={{ fontWeight: 900, color: p.color, fontSize: 32, lineHeight: 1, marginBottom: 4 }}>{p.price}</div>
                <div style={{ color: "rgba(255,255,255,.4)", fontSize: 11, marginBottom: 14 }}>{p.period || "forever"}</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,.65)", lineHeight: 1.6, marginBottom: 14 }}>{p.features[0]}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 22 }}>
            <Btn onClick={() => setShowAuth("signup")} color={B.pink} style={{ padding: "12px 26px", fontSize: 13 }}>Give me my 5 free sessions →</Btn>
          </div>
        </div>
      )}

      {/* FINAL CTA */}
      {!user && (
        <div style={{ padding: "90px 20px 100px", textAlign: "center", background: "linear-gradient(135deg, rgba(255,45,120,.10), rgba(0,200,232,.06))" }}>
          <div style={{ maxWidth: 660, margin: "0 auto" }}>
            <h2 style={{ color: "#fff", fontWeight: 900, fontSize: "clamp(28px,5.5vw,44px)", letterSpacing: -1.4, lineHeight: 1.1, marginBottom: 18 }}>Your nursing career <span style={{ color: B.pink }}>starts here.</span></h2>
            <p style={{ color: "rgba(255,255,255,.72)", fontSize: 16, marginBottom: 32, lineHeight: 1.75 }}>Founding members get lifetime access to ShiftMateRN for $97 — one time — no monthly fees — ever. All 24 tools. Every future tool I build. Built by a cardiac nurse with 19 years experience. When founding member pricing ends, the price goes up to $12/month forever.</p>
            <Btn onClick={() => setShowAuth("signup")} color={B.pink} style={{ padding: "16px 40px", fontSize: 16 }}>Start free in 30 seconds →</Btn>
            <div style={{ marginTop: 16, fontSize: 12, color: "rgba(255,255,255,.4)" }}>$97 lifetime · never pay again</div>
          </div>
        </div>
      )}

      <Foot />
      <Modals /><CSS />
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: B.navy, fontFamily: "'Poppins',sans-serif", display: "flex", flexDirection: "column" }}>
      <Nav />
      <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 12, borderBottom: "1px solid rgba(255,255,255,.07)", background: B.navy2 }}>
        <button onClick={() => setScreen("home")} style={{ background: "rgba(255,255,255,.07)", border: "none", borderRadius: 8, padding: "7px 14px", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "'Poppins',sans-serif" }}>← Tools</button>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: tool?.color + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>{tool?.icon}</div>
        <div>
          <div style={{ fontWeight: 800, color: "#fff", fontSize: 15 }}>{tool?.title}</div>
          <div style={{ fontSize: 10, color: tool?.color, fontWeight: 700, letterSpacing: 1 }}>{tool?.sub}</div>
        </div>
        {!isPro && <div style={{ marginLeft: "auto", fontSize: 11, color: "rgba(255,255,255,.3)", fontWeight: 600 }}>{sessLeft} sessions left</div>}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
        {history.length === 0 && (
          <div style={{ textAlign: "center", padding: "50px 20px", color: "rgba(255,255,255,.2)", fontSize: 14, lineHeight: 1.7 }}>
            {tool?.desc}<br /><br /><span style={{ fontSize: 12 }}>Ask away — no judgment here 💕</span>
          </div>
        )}
        {history.map((msg, i) => (
          <div key={i} style={{ marginBottom: 16 }}>
            {msg.role === "user"
              ? <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{ maxWidth: "80%", background: tool?.color, color: tool?.color === B.yellow ? B.navy : "#fff", borderRadius: "18px 18px 4px 18px", padding: "13px 17px", fontSize: 14, fontWeight: 800, lineHeight: 1.7 }}>
                    {msg.image && <img src={msg.image.preview || msg.image} alt="uploaded" style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 10, marginBottom: msg.content ? 8 : 0, display: "block" }} />}
                    {msg.content}
                  </div>
                </div>
              : <div style={{ maxWidth: "88%" }}>
                  {(() => {
                    if (tool?.id === "concept_map") {
                      try {
                        const cm = JSON.parse(msg.content);
                        if (cm.nodes && cm.edges) return <ConceptMapView data={cm} />;
                      } catch(e) {}
                    }
                    return <div style={{ background: "rgba(255,255,255,.05)", borderRadius: "4px 18px 18px 18px", padding: "14px 18px", fontSize: 14, color: "rgba(255,255,255,.9)", lineHeight: 1.8 }}><MarkdownText>{msg.content}</MarkdownText></div>;
                  })()}
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,.32)", marginTop: 6, fontStyle: "italic", paddingLeft: 4 }}>Educational tool — verify with your preceptor, charge nurse, or supervising clinician before acting on any clinical guidance.</div>
                  <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    <button onClick={() => copyText(i, msg.content)} style={ABS}>{copied === i ? "✓ Copied" : "📋 Copy"}</button>
                    <button onClick={() => shareMsg(i, msg.content)} style={ABS}>{sharedFlash === i ? "✅ Shared!" : "🔗 Share"}</button>
                    <button onClick={() => saveMsg(i, msg)} style={ABS}>{savedFlash === i ? "✓ Saved" : "🔖 Save"}</button>
                    <button onClick={() => openPrint(tool.title, msg.question, msg.content)} style={ABS}>🖨️ Print</button>
                    <button onClick={() => toggleSpeak(i, msg.content)} style={{ ...ABS, color: speakingId === i ? B.pink : "rgba(255,255,255,.5)" }}>{speakingId === i ? "⏸ Stop" : "🔊 Read"}</button>
                  </div>
                  <FeedbackButtons msgIdx={i} toolId={tool?.id} msgContent={msg.content} userId={user?.id} />
                </div>
            }
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "13px 17px", background: "rgba(255,255,255,.05)", borderRadius: "4px 18px 18px 18px", width: "fit-content" }}>
            <div style={{ display: "flex", gap: 5 }}>
              {[0, 1, 2].map(i => <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: tool?.color, animation: `bounce 1.2s ${i * .2}s infinite` }} />)}
            </div>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,.4)", fontStyle: "italic" }}>{loadingMessages[tool?.id] || "Thinking..."}</span>
          </div>
        )}
        {error && <div style={{ color: B.pink, fontSize: 13, padding: "8px 0" }}>{error}</div>}
      </div>

      {pendingImage && (
        <div style={{ padding: "10px 18px 0", borderTop: "1px solid rgba(255,255,255,.07)" }}>
          <div style={{ background: "rgba(255,45,120,.1)", border: "1px solid rgba(255,45,120,.3)", borderRadius: 10, padding: "8px 12px", marginBottom: 8, fontSize: 11, color: "rgba(255,255,255,.8)", lineHeight: 1.5 }}>
            ⚠️ <strong>No PHI:</strong> crop out patient names, MRNs, dates of birth, or any identifying details before sending.
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src={pendingImage.preview} alt="preview" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, border: "1px solid rgba(255,255,255,.15)" }} />
            <div style={{ flex: 1, fontSize: 12, color: "rgba(255,255,255,.6)" }}>Image attached. Add a question or just press send.</div>
            <button onClick={() => setPendingImage(null)} style={{ background: "rgba(255,255,255,.08)", border: "none", borderRadius: 8, padding: "6px 10px", color: "rgba(255,255,255,.7)", fontSize: 12, cursor: "pointer", fontFamily: "'Poppins',sans-serif" }}>✕ Remove</button>
          </div>
        </div>
      )}
      {imgErr && <div style={{ color: B.pink, fontSize: 12, padding: "8px 18px 0" }}>{imgErr}</div>}

      <div style={{ padding: "8px 18px 0" }}><PHIBanner /></div>
      <div style={{ padding: "14px 18px", borderTop: "1px solid rgba(255,255,255,.07)", display: "flex", gap: 8, alignItems: "stretch" }}>
        <input id="rn101-img-input" type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { handleImageFile(e.target.files?.[0]); e.target.value = ""; }} />
        <button
          onClick={() => document.getElementById("rn101-img-input")?.click()}
          title="Attach an image (EKG strip, lab screen, chart, etc.)"
          style={{ background: pendingImage ? tool?.color + "33" : "rgba(255,255,255,.06)", border: "1.5px solid rgba(255,255,255,.1)", borderRadius: 14, padding: "0 12px", color: "#fff", fontSize: 18, cursor: "pointer", fontFamily: "'Poppins',sans-serif" }}
        >📎</button>
        <button
          onClick={toggleVoiceInput}
          title={recording ? "Stop recording" : "Speak your question instead of typing"}
          style={{ background: recording ? B.pink : "rgba(255,255,255,.06)", border: `1.5px solid ${recording ? B.pink : "rgba(255,255,255,.1)"}`, borderRadius: 14, padding: "0 12px", color: "#fff", fontSize: 18, cursor: "pointer", fontFamily: "'Poppins',sans-serif", animation: recording ? "pulse 1.5s ease-in-out infinite" : "none" }}
        >{recording ? "⏺" : "🎙"}</button>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
          placeholder={pendingImage ? "Ask about this image — or just press send..." : tool?.ph}
          rows={3}
          style={{ flex: 1, background: "rgba(255,255,255,.05)", border: "1.5px solid rgba(255,255,255,.1)", borderRadius: 14, padding: "13px 15px", color: "#fff", fontSize: 14, fontFamily: "'Poppins',sans-serif", resize: "none", outline: "none", lineHeight: 1.6 }}
        />
        <button
          onClick={submit}
          disabled={loading || (!input.trim() && !pendingImage)}
          style={{ background: loading || (!input.trim() && !pendingImage) ? "rgba(255,255,255,.08)" : tool?.color, color: tool?.color === B.yellow ? B.navy : "#fff", border: "none", borderRadius: 14, padding: "0 20px", fontWeight: 800, fontSize: 18, cursor: loading || (!input.trim() && !pendingImage) ? "default" : "pointer", transition: "all .2s" }}
        >→</button>
      </div>
      <SamplePrompts toolId={tool?.id} onSelect={p => { setInput(p); }} />
      <Modals /><CSS />
    </div>
  );
}
